import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export type Language = 'en' | 'de' | 'tr';

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  autoTranslate: boolean;
  setAutoTranslate: (enabled: boolean) => void;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface Translations {
  [key: string]: {
    en: string;
    de: string;
    tr: string;
  };
}

// Translation keys - organized by component
const translations: Translations = {
  // Header Component
  'header.home': {
    en: 'Home',
    de: 'Startseite',
    tr: 'Ana Sayfa',
  },
  'header.products': {
    en: 'Products',
    de: 'Produkte',
    tr: 'Ürünler',
  },
  'header.categories': {
    en: 'Categories',
    de: 'Kategorien',
    tr: 'Kategoriler',
  },
  'header.jobs': {
    en: 'Jobs',
    de: 'Stellenangebote',
    tr: 'İşler',
  },
  'header.jobCategories': {
    en: 'Job Categories',
    de: 'Stellenkategorien',
    tr: 'İş Kategorileri',
  },
  'header.companies': {
    en: 'Companies',
    de: 'Unternehmen',
    tr: 'Şirketler',
  },
  'header.searchProducts': {
    en: 'Search for products...',
    de: 'Nach Produkten suchen...',
    tr: 'Ürün ara...',
  },
  'header.searchJobs': {
    en: 'Search for jobs...',
    de: 'Nach Stellenangeboten suchen...',
    tr: 'İş ara...',
  },
  'header.findProducts': {
    en: 'Find products',
    de: 'Produkte finden',
    tr: 'Ürünleri bul',
  },
  'header.findLocation': {
    en: 'Find location',
    de: 'Ort finden',
    tr: 'Konum bul',
  },
  'header.findOffers': {
    en: 'Find offers',
    de: 'Angebote finden',
    tr: 'Teklifleri bul',
  },
  'header.findMembers': {
    en: 'Find members',
    de: 'Mitglieder finden',
    tr: 'Üyeleri bul',
  },
  'header.findHelp': {
    en: 'Find help',
    de: 'Hilfe finden',
    tr: 'Yardım bul',
  },
  'header.findCompanies': {
    en: 'Find companies',
    de: 'Unternehmen finden',
    tr: 'Şirketleri bul',
  },
  'header.findJobs': {
    en: 'Find jobs',
    de: 'Jobs finden',
    tr: 'İşleri bul',
  },
  'header.dashboard': {
    en: 'Dashboard',
    de: 'Dashboard',
    tr: 'Kontrol Paneli',
  },
  'header.profile': {
    en: 'Profile',
    de: 'Profil',
    tr: 'Profil',
  },
  'header.orders': {
    en: 'Orders',
    de: 'Bestellungen',
    tr: 'Siparişler',
  },
  'header.interviews': {
    en: 'Interviews',
    de: 'Bewerbungsgespräche',
    tr: 'Mülakatlar',
  },
  'header.sellerDashboard': {
    en: 'Seller Dashboard',
    de: 'Verkäufer-Dashboard',
    tr: 'Satıcı Paneli',
  },
  'header.messages': {
    en: 'Messages',
    de: 'Nachrichten',
    tr: 'Mesajlar',
  },
  'header.logout': {
    en: 'Logout',
    de: 'Abmelden',
    tr: 'Çıkış Yap',
  },
  'header.login': {
    en: 'Login',
    de: 'Anmelden',
    tr: 'Giriş Yap',
  },
  'header.signUp': {
    en: 'Sign Up',
    de: 'Registrieren',
    tr: 'Kayıt Ol',
  },
  'header.jobAIWriter': {
    en: 'Job AI Writer',
    de: 'KI-Bewerbungsschreiber',
    tr: 'İş AI Yazıcı',
  },
  'header.restoreJobAIWriter': {
    en: 'Restore Job AI Writer',
    de: 'KI-Bewerbungsschreiber wiederherstellen',
    tr: 'İş AI Yazıcıyı Geri Yükle',
  },
  'header.openJobAIWriter': {
    en: 'Open Job AI Writer',
    de: 'KI-Bewerbungsschreiber öffnen',
    tr: 'İş AI Yazıcıyı Aç',
  },
  // HomePage Hero Section
  'home.hero.slogan1': {
    en: 'Discover Amazing Fashion',
    de: 'Entdecke fantastische Mode',
    tr: 'Harika Modayı Keşfet',
  },
  'home.hero.slogan2': {
    en: 'Elevate Your Style',
    de: 'Erhöhe deinen Stil',
    tr: 'Stilini Yükselt',
  },
  'home.hero.slogan3': {
    en: 'Shop. Sell. Shine.',
    de: 'Einkaufen. Verkaufen. Glänzen.',
    tr: 'Al. Sat. Parlat.',
  },
  'home.hero.slogan4': {
    en: 'Trends You\'ll Love',
    de: 'Trends, die du lieben wirst',
    tr: 'Seveceğin Trendler',
  },
  'home.hero.slogan5': {
    en: 'Fashion for Every You',
    de: 'Mode für jeden von euch',
    tr: 'Herkes İçin Moda',
  },
  'home.hero.subtitle': {
    en: 'Shop the latest trends from top brands and independent sellers. Buy and sell with confidence.',
    de: 'Shoppe die neuesten Trends von Top-Marken und unabhängigen Verkäufern. Kaufe und verkaufe mit Vertrauen.',
    tr: 'En iyi markalardan ve bağımsız satıcılardan en son trendleri satın alın. Güvenle alın ve satın.',
  },
  'home.hero.shopNow': {
    en: 'Shop Now',
    de: 'Jetzt einkaufen',
    tr: 'Şimdi Alışveriş Yap',
  },
  'home.hero.startSelling': {
    en: 'Start Selling',
    de: 'Verkaufen beginnen',
    tr: 'Satışa Başla',
  },
  // HomePage Categories Section
  'home.categories.title': {
    en: 'Shop by Category',
    de: 'Nach Kategorie einkaufen',
    tr: 'Kategoriye Göre Alışveriş',
  },
  'home.categories.subtitle': {
    en: 'Find exactly what you\'re looking for',
    de: 'Finde genau das, wonach du suchst',
    tr: 'Tam olarak aradığınızı bulun',
  },
  'home.categories.viewAll': {
    en: 'View All Categories',
    de: 'Alle Kategorien anzeigen',
    tr: 'Tüm Kategorileri Görüntüle',
  },
  'home.categories.comingSoon': {
    en: 'Coming soon',
    de: 'Demnächst verfügbar',
    tr: 'Yakında',
  },
  'home.categories.items': {
    en: 'items',
    de: 'Artikel',
    tr: 'ürün',
  },
  'home.categories.retry': {
    en: 'Retry',
    de: 'Wiederholen',
    tr: 'Tekrar Dene',
  },
  'home.categories.noCategories': {
    en: 'No categories available at the moment.',
    de: 'Derzeit sind keine Kategorien verfügbar.',
    tr: 'Şu anda kategori bulunmuyor.',
  },
  'home.categories.failedToLoad': {
    en: 'Failed to load categories',
    de: 'Kategorien konnten nicht geladen werden',
    tr: 'Kategoriler yüklenemedi',
  },
  // HomePage Shop by Location Section
  'home.location.title': {
    en: 'Shop by Location',
    de: 'Nach Standort einkaufen',
    tr: 'Konuma Göre Alışveriş',
  },
  'home.location.subtitle': {
    en: 'Discover amazing products from sellers in your area. Find local treasures and support your community!',
    de: 'Entdecke tolle Produkte von Verkäufern in deiner Nähe. Finde lokale Schätze und unterstütze deine Community!',
    tr: 'Bölgenizdeki satıcılardan harika ürünler keşfedin. Yerel hazineleri bulun ve topluluğunuzu destekleyin!',
  },
  'home.location.features.localDiscovery.title': {
    en: 'Local Discovery',
    de: 'Lokale Entdeckung',
    tr: 'Yerel Keşif',
  },
  'home.location.features.localDiscovery.desc': {
    en: 'Find products from sellers near you and discover unique local items.',
    de: 'Finde Produkte von Verkäufern in deiner Nähe und entdecke einzigartige lokale Artikel.',
    tr: 'Size yakın satıcılardan ürünleri bulun ve benzersiz yerel ürünleri keşfedin.',
  },
  'home.location.features.supportLocal.title': {
    en: 'Support Local',
    de: 'Lokales unterstützen',
    tr: 'Yereli Destekle',
  },
  'home.location.features.supportLocal.desc': {
    en: 'Support your local community by shopping from nearby sellers.',
    de: 'Unterstütze deine lokale Gemeinschaft, indem du bei Händlern in deiner Nähe einkaufst.',
    tr: 'Yakınınızdaki satıcılardan alışveriş yaparak yerel topluluğunuzu destekleyin.',
  },
  'home.location.features.interactiveMap.title': {
    en: 'Interactive Map',
    de: 'Interaktive Karte',
    tr: 'Etkileşimli Harita',
  },
  'home.location.features.interactiveMap.desc': {
    en: 'Explore our interactive map to find products in any location.',
    de: 'Erkunde unsere interaktive Karte, um Produkte an jedem Ort zu finden.',
    tr: 'Her konumdaki ürünleri bulmak için etkileşimli haritamızı keşfedin.',
  },
  'home.location.cta': {
    en: 'Explore Map',
    de: 'Karte erkunden',
    tr: 'Haritayı Keşfet',
  },
  'home.location.ctaHelper': {
    en: 'Click to open our interactive map and start exploring!',
    de: 'Klicke, um unsere interaktive Karte zu öffnen und mit der Erkundung zu beginnen!',
    tr: 'Etkileşimli haritamızı açmak ve keşfetmeye başlamak için tıklayın!',
  },
  // HomePage Featured Products Section
  'home.featured.title': {
    en: 'Featured Products',
    de: 'Empfohlene Produkte',
    tr: 'Öne Çıkan Ürünler',
  },
  'home.featured.subtitle': {
    en: 'Handpicked items just for you',
    de: 'Nur für dich handverlesene Artikel',
    tr: 'Sizin için özenle seçildi',
  },
  'home.featured.viewAll': {
    en: 'View All Products',
    de: 'Alle Produkte anzeigen',
    tr: 'Tüm Ürünleri Görüntüle',
  },
  'home.featured.addToCart': {
    en: 'Add to Cart',
    de: 'In den Warenkorb',
    tr: 'Sepete Ekle',
  },
  'home.featured.new': {
    en: 'NEW',
    de: 'NEU',
    tr: 'YENİ',
  },
  // HomePage CTA Section
  'home.cta.title': {
    en: 'Ready to Start Selling?',
    de: 'Bereit, mit dem Verkaufen zu beginnen?',
    tr: 'Satışa Başlamaya Hazır mısınız?',
  },
  'home.cta.subtitle': {
    en: 'Join thousands of sellers and start your fashion business today',
    de: 'Schließe dich Tausenden von Verkäufern an und starte noch heute dein Modegeschäft',
    tr: 'Binlerce satıcıya katılın ve moda işinizi bugün başlatın',
  },
  'home.cta.button': {
    en: 'Become a Seller',
    de: 'Verkäufer werden',
    tr: 'Satıcı Ol',
  },
  // Footer
  'footer.company.blurb': {
    en: 'Your ultimate destination for fashion. Discover amazing clothes from top brands and independent sellers. Buy and sell with confidence.',
    de: 'Dein ultimatives Ziel für Mode. Entdecke großartige Kleidung von Top-Marken und unabhängigen Verkäufern. Kaufe und verkaufe mit Vertrauen.',
    tr: 'Moda için nihai adresiniz. En iyi markalardan ve bağımsız satıcılardan harika kıyafetleri keşfedin. Güvenle alıp satın.',
  },
  'footer.sections.shop.title': {
    en: 'Shop',
    de: 'Shop',
    tr: 'Mağaza',
  },
  'footer.sections.shop.women': {
    en: 'Women',
    de: 'Damen',
    tr: 'Kadın',
  },
  'footer.sections.shop.men': {
    en: 'Men',
    de: 'Herren',
    tr: 'Erkek',
  },
  'footer.sections.shop.kids': {
    en: 'Kids',
    de: 'Kinder',
    tr: 'Çocuk',
  },
  'footer.sections.shop.accessories': {
    en: 'Accessories',
    de: 'Accessoires',
    tr: 'Aksesuarlar',
  },
  'footer.sections.shop.sale': {
    en: 'Sale',
    de: 'Sale',
    tr: 'İndirim',
  },
  'footer.sections.service.title': {
    en: 'Customer Service',
    de: 'Kundenservice',
    tr: 'Müşteri Hizmetleri',
  },
  'footer.sections.service.contact': {
    en: 'Contact Us',
    de: 'Kontakt',
    tr: 'İletişim',
  },
  'footer.sections.service.sizeGuide': {
    en: 'Size Guide',
    de: 'Größentabelle',
    tr: 'Beden Rehberi',
  },
  'footer.sections.service.shipping': {
    en: 'Shipping Info',
    de: 'Versandinformationen',
    tr: 'Kargo Bilgileri',
  },
  'footer.sections.service.returns': {
    en: 'Returns',
    de: 'Rücksendungen',
    tr: 'İadeler',
  },
  'footer.sections.service.faq': {
    en: 'FAQ',
    de: 'FAQ',
    tr: 'SSS',
  },
  'footer.sections.company.title': {
    en: 'Company',
    de: 'Unternehmen',
    tr: 'Şirket',
  },
  'footer.sections.company.about': {
    en: 'About Us',
    de: 'Über uns',
    tr: 'Hakkımızda',
  },
  'footer.sections.company.careers': {
    en: 'Careers',
    de: 'Karriere',
    tr: 'Kariyer',
  },
  'footer.sections.company.press': {
    en: 'Press',
    de: 'Presse',
    tr: 'Basın',
  },
  'footer.sections.company.sustainability': {
    en: 'Sustainability',
    de: 'Nachhaltigkeit',
    tr: 'Sürdürülebilirlik',
  },
  'footer.sections.company.investors': {
    en: 'Investor Relations',
    de: 'Investor Relations',
    tr: 'Yatırımcı İlişkileri',
  },
  'footer.sections.sell.title': {
    en: 'Sell on Sistasouk',
    de: 'Bei Sistasouk verkaufen',
    tr: 'Sistasouk’ta Satış Yap',
  },
  'footer.sections.sell.start': {
    en: 'Start Selling',
    de: 'Verkaufen beginnen',
    tr: 'Satışa Başla',
  },
  'footer.sections.sell.center': {
    en: 'Seller Center',
    de: 'Verkäuferzentrum',
    tr: 'Satıcı Merkezi',
  },
  'footer.sections.sell.guidelines': {
    en: 'Seller Guidelines',
    de: 'Verkäuferrichtlinien',
    tr: 'Satıcı Kuralları',
  },
  'footer.sections.sell.fees': {
    en: 'Fees & Pricing',
    de: 'Gebühren & Preise',
    tr: 'Ücretler ve Fiyatlandırma',
  },
  'footer.sections.sell.support': {
    en: 'Seller Support',
    de: 'Verkäufer-Support',
    tr: 'Satıcı Desteği',
  },
  'footer.legal.privacy': {
    en: 'Privacy Policy',
    de: 'Datenschutz',
    tr: 'Gizlilik Politikası',
  },
  'footer.legal.terms': {
    en: 'Terms of Service',
    de: 'Nutzungsbedingungen',
    tr: 'Hizmet Şartları',
  },
  'footer.legal.cookies': {
    en: 'Cookie Policy',
    de: 'Cookie-Richtlinie',
    tr: 'Çerez Politikası',
  },
  'footer.legal.accessibility': {
    en: 'Accessibility',
    de: 'Barrierefreiheit',
    tr: 'Erişilebilirlik',
  },
  'footer.admin.signIn': {
    en: 'Admin Sign In',
    de: 'Admin-Anmeldung',
    tr: 'Yönetici Girişi',
  },
  'footer.bottom.allRightsReserved': {
    en: 'All rights reserved.',
    de: 'Alle Rechte vorbehalten.',
    tr: 'Tüm hakları saklıdır.',
  },
  'footer.social.facebook': {
    en: 'Facebook',
    de: 'Facebook',
    tr: 'Facebook',
  },
  'footer.social.twitter': {
    en: 'Twitter',
    de: 'Twitter',
    tr: 'Twitter',
  },
  'footer.social.instagram': {
    en: 'Instagram',
    de: 'Instagram',
    tr: 'Instagram',
  },
  'footer.social.youtube': {
    en: 'YouTube',
    de: 'YouTube',
    tr: 'YouTube',
  },
  // Auth Modal & Forms
  'auth.title': {
    en: 'Welcome to FashionHub',
    de: 'Willkommen bei FashionHub',
    tr: 'FashionHub’a Hoş Geldiniz',
  },
  'auth.tabs.signIn': {
    en: 'Sign In',
    de: 'Anmelden',
    tr: 'Giriş Yap',
  },
  'auth.tabs.signUp': {
    en: 'Sign Up',
    de: 'Registrieren',
    tr: 'Kayıt Ol',
  },
  'auth.social.google': {
    en: 'Continue with Google',
    de: 'Mit Google fortfahren',
    tr: 'Google ile devam et',
  },
  'auth.social.facebook': {
    en: 'Continue with Facebook',
    de: 'Mit Facebook fortfahren',
    tr: 'Facebook ile devam et',
  },
  'auth.orSignInWithEmail': {
    en: 'or sign in with email',
    de: 'oder mit E-Mail anmelden',
    tr: 'veya e‑posta ile giriş yapın',
  },
  'auth.orSignUpWithEmail': {
    en: 'or sign up with email',
    de: 'oder mit E-Mail registrieren',
    tr: 'veya e‑posta ile kayıt olun',
  },
  'auth.email': {
    en: 'Email Address',
    de: 'E-Mail-Adresse',
    tr: 'E‑posta Adresi',
  },
  'auth.password': {
    en: 'Password',
    de: 'Passwort',
    tr: 'Şifre',
  },
  'auth.forgotPassword': {
    en: 'Forgot your password?',
    de: 'Passwort vergessen?',
    tr: 'Şifrenizi mi unuttunuz?',
  },
  'auth.signIn': {
    en: 'Sign In',
    de: 'Anmelden',
    tr: 'Giriş Yap',
  },
  'auth.signingIn': {
    en: 'Signing In...',
    de: 'Anmeldung läuft...',
    tr: 'Giriş yapılıyor...',
  },
  'auth.noAccount': {
    en: "Don't have an account?",
    de: 'Haben Sie noch kein Konto?',
    tr: 'Hesabınız yok mu?',
  },
  'auth.signUpHere': {
    en: 'Sign up here',
    de: 'Hier registrieren',
    tr: 'Buradan kayıt olun',
  },
  'auth.firstName': {
    en: 'First Name',
    de: 'Vorname',
    tr: 'Ad',
  },
  'auth.lastName': {
    en: 'Last Name',
    de: 'Nachname',
    tr: 'Soyad',
  },
  'auth.confirmPassword': {
    en: 'Confirm Password',
    de: 'Passwort bestätigen',
    tr: 'Şifreyi Onayla',
  },
  'auth.alias': {
    en: 'Alias',
    de: 'Alias',
    tr: 'Takma Ad',
  },
  'auth.alias.placeholder': {
    en: 'Choose a unique username',
    de: 'Wähle einen eindeutigen Benutzernamen',
    tr: 'Benzersiz bir kullanıcı adı seçin',
  },
  'auth.alias.helper': {
    en: 'This will be your public display name',
    de: 'Dies ist dein öffentlicher Anzeigename',
    tr: 'Bu, herkese açık görünen adınız olacaktır',
  },
  'auth.createAccount': {
    en: 'Create Account',
    de: 'Konto erstellen',
    tr: 'Hesap Oluştur',
  },
  'auth.creatingAccount': {
    en: 'Creating Account...',
    de: 'Konto wird erstellt...',
    tr: 'Hesap oluşturuluyor...',
  },
  'auth.alreadyHaveAccount': {
    en: 'Already have an account?',
    de: 'Haben Sie bereits ein Konto?',
    tr: 'Zaten bir hesabınız var mı?',
  },
  'auth.signInHere': {
    en: 'Sign in here',
    de: 'Hier anmelden',
    tr: 'Buradan giriş yapın',
  },
  'auth.agree': {
    en: 'By continuing, you agree to our Terms of Service and Privacy Policy',
    de: 'Indem du fortfährst, stimmst du unseren Nutzungsbedingungen und unserer Datenschutzrichtlinie zu',
    tr: 'Devam ederek, Hizmet Şartlarımızı ve Gizlilik Politikamızı kabul etmiş olursunuz',
  },
  // All Products Page
  'allProducts.title': {
    en: 'All Products',
    de: 'Alle Produkte',
    tr: 'Tüm Ürünler',
  },
  'allProducts.subtitle': {
    en: 'Discover amazing products from trusted sellers',
    de: 'Entdecke großartige Produkte von vertrauenswürdigen Verkäufern',
    tr: 'Güvenilir satıcılardan harika ürünleri keşfedin',
  },
  'allProducts.jobsTitle': {
    en: 'All Jobs',
    de: 'Alle Jobs',
    tr: 'Tüm İş İlanları',
  },
  'allProducts.jobsSubtitle': {
    en: 'Find your next career opportunity with top companies',
    de: 'Finde deine nächste Karrierechance bei Top-Unternehmen',
    tr: 'Önde gelen şirketlerde bir sonraki kariyer fırsatınızı bulun',
  },
  'allProducts.notice.products': {
    en: 'Showing products only - filters cleared for better browsing',
    de: 'Nur Produkte werden angezeigt – bessere Übersicht durch zurückgesetzte Filter',
    tr: 'Yalnızca ürünler gösteriliyor – daha iyi gezinme için filtreler temizlendi',
  },
  'allProducts.notice.jobs': {
    en: 'Showing job postings only - filters cleared for better browsing',
    de: 'Nur Stellenangebote werden angezeigt – bessere Übersicht durch zurückgesetzte Filter',
    tr: 'Yalnızca iş ilanları gösteriliyor – daha iyi gezinme için filtreler temizlendi',
  },
  'allProducts.search.placeholder.products': {
    en: 'Search products...',
    de: 'Produkte suchen...',
    tr: 'Ürün ara...',
  },
  'allProducts.search.placeholder.jobs': {
    en: 'Search jobs...',
    de: 'Jobs suchen...',
    tr: 'İş ara...',
  },
  'allProducts.filters.category': {
    en: 'Category',
    de: 'Kategorie',
    tr: 'Kategori',
  },
  'allProducts.filters.sortBy': {
    en: 'Sort By',
    de: 'Sortieren nach',
    tr: 'Sırala',
  },
  'allProducts.chips.search': {
    en: 'Search',
    de: 'Suche',
    tr: 'Arama',
  },
  'allProducts.chips.category': {
    en: 'Category',
    de: 'Kategorie',
    tr: 'Kategori',
  },
  'allProducts.chips.seller': {
    en: 'Seller',
    de: 'Verkäufer',
    tr: 'Satıcı',
  },
  'allProducts.chips.brands': {
    en: 'Brands',
    de: 'Marken',
    tr: 'Markalar',
  },
  'allProducts.chips.inStockOnly': {
    en: 'In Stock Only',
    de: 'Nur auf Lager',
    tr: 'Sadece Stoktakiler',
  },
  'allProducts.chips.onSaleOnly': {
    en: 'On Sale Only',
    de: 'Nur im Angebot',
    tr: 'Sadece İndirimdekiler',
  },
  'allProducts.chips.ratingSuffix': {
    en: 'stars',
    de: 'Sterne',
    tr: 'yıldız',
  },
  'allProducts.chips.condition': {
    en: 'Condition',
    de: 'Zustand',
    tr: 'Durum',
  },
  'allProducts.chips.skills': {
    en: 'Skills',
    de: 'Fähigkeiten',
    tr: 'Yetenekler',
  },
  'allProducts.chips.tag': {
    en: 'Tag',
    de: 'Tag',
    tr: 'Etiket',
  },
  'allProducts.apply': {
    en: 'Apply',
    de: 'Anwenden',
    tr: 'Uygula',
  },
  'allProducts.moreFilters': {
    en: 'More Filters',
    de: 'Weitere Filter',
    tr: 'Daha Fazla Filtre',
  },
  'allProducts.searchOnMap': {
    en: 'Search on Map',
    de: 'Auf der Karte suchen',
    tr: 'Haritada Ara',
  },
  'allProducts.allCategories': {
    en: 'All Categories',
    de: 'Alle Kategorien',
    tr: 'Tüm Kategoriler',
  },
  'allProducts.loadingCategories': {
    en: 'Loading categories...',
    de: 'Kategorien werden geladen...',
    tr: 'Kategoriler yükleniyor...',
  },
  'allProducts.errorLoadingCategories': {
    en: 'Error loading categories',
    de: 'Fehler beim Laden der Kategorien',
    tr: 'Kategoriler yüklenirken hata oluştu',
  },
  'allProducts.chips.rating': {
    en: 'Rating',
    de: 'Bewertung',
    tr: 'Puan',
  },
  'allProducts.sort.relevance': {
    en: 'Relevance',
    de: 'Relevanz',
    tr: 'Alaka Düzeyi',
  },
  'allProducts.sort.priceLow': {
    en: 'Price: Low to High',
    de: 'Preis: Aufsteigend',
    tr: 'Fiyat: Düşükten Yükseğe',
  },
  'allProducts.sort.priceHigh': {
    en: 'Price: High to Low',
    de: 'Preis: Absteigend',
    tr: 'Fiyat: Yüksekten Düşüğe',
  },
  'allProducts.sort.rating': {
    en: 'Highest Rated',
    de: 'Bestbewertet',
    tr: 'En Yüksek Puanlı',
  },
  'allProducts.sort.newest': {
    en: 'Newest First',
    de: 'Neueste zuerst',
    tr: 'En Yeni Önce',
  },
  'allProducts.sort.popular': {
    en: 'Most Popular',
    de: 'Am beliebtesten',
    tr: 'En Popüler',
  },
  // Common
  'common.showing': { en: 'Showing', de: 'Anzeige', tr: 'Gösteriliyor' },
  'common.to': { en: 'to', de: 'bis', tr: 'ile' },
  'common.of': { en: 'of', de: 'von', tr: '/' },
  'common.save': { en: 'Save', de: 'Speichern', tr: 'Kaydet' },
  'common.saving': { en: 'Saving...', de: 'Speichern...', tr: 'Kaydediliyor...' },
  'common.saved': { en: 'Saved', de: 'Gespeichert', tr: 'Kaydedildi' },
  'common.save_failed': { en: 'Save failed', de: 'Speichern fehlgeschlagen', tr: 'Kaydetme başarısız' },
  // Notifications - Push test
  'settings.notifications.push.testButton': { en: 'Test Push Notification', de: 'Push-Benachrichtigung testen', tr: 'Bildirim Testi' },
  'settings.notifications.push.unsupported': { en: 'Notifications are not supported in this browser.', de: 'Benachrichtigungen werden in diesem Browser nicht unterstützt.', tr: 'Bildirimler bu tarayıcıda desteklenmiyor.' },
  'settings.notifications.push.permissionBlocked': { en: 'Notification permission is blocked in browser settings.', de: 'Benachrichtigungsberechtigung ist in den Browsereinstellungen blockiert.', tr: 'Bildirim izni tarayıcı ayarlarında engellenmiş.' },
  'settings.notifications.push.permissionDenied': { en: 'Notification permission denied.', de: 'Benachrichtigungsberechtigung abgelehnt.', tr: 'Bildirim izni reddedildi.' },
  'settings.notifications.push.testTitle': { en: 'Push Notification Test', de: 'Push-Benachrichtigungs-Test', tr: 'Bildirim Testi' },
  'settings.notifications.push.testBody': { en: "This is a test notification from your settings.", de: 'Dies ist eine Testbenachrichtigung aus deinen Einstellungen.', tr: 'Bu, ayarlarınızdan bir test bildirimidir.' },
  'settings.notifications.push.sent': { en: 'Test notification sent.', de: 'Testbenachrichtigung gesendet.', tr: 'Test bildirimi gönderildi.' },
  'settings.notifications.push.testButtonDelayed': { en: 'Test after 5s', de: 'In 5 Sek. testen', tr: '5 sn sonra test' },
  'settings.notifications.push.countdownPrefix': { en: 'Starting in', de: 'Start in', tr: 'Başlıyor:' },
  'settings.notifications.push.secondsShort': { en: 's', de: 's', tr: ' sn' },
  // All Products Pagination
  'allProducts.pagination.productsFound': {
    en: 'products found',
    de: 'Produkte gefunden',
    tr: 'ürün bulundu',
  },
  'allProducts.pagination.jobsFound': {
    en: 'jobs found',
    de: 'Jobs gefunden',
    tr: 'iş ilanı bulundu',
  },
  'allProducts.pagination.pages': {
    en: 'pages',
    de: 'Seiten',
    tr: 'sayfa',
  },
  'allProducts.pagination.itemsPerPage': {
    en: 'Items per page:',
    de: 'Elemente pro Seite:',
    tr: 'Sayfa başına öğe:',
  },
  'allProducts.pagination.first': { en: 'First', de: 'Erste', tr: 'İlk' },
  'allProducts.pagination.previous': { en: 'Previous', de: 'Zurück', tr: 'Önceki' },
  'allProducts.pagination.next': { en: 'Next', de: 'Weiter', tr: 'Sonraki' },
  'allProducts.pagination.last': { en: 'Last', de: 'Letzte', tr: 'Son' },
  'allProducts.pagination.goToPage': {
    en: 'Go to page:',
    de: 'Gehe zu Seite:',
    tr: 'Sayfaya git:',
  },
  // All Products Features Section (bottom)
  'allProducts.features.freeShipping.title': {
    en: 'Free Shipping',
    de: 'Kostenloser Versand',
    tr: 'Ücretsiz Kargo',
  },
  'allProducts.features.freeShipping.subtitle': {
    en: 'On orders over $50',
    de: 'Bei Bestellungen über 50 $',
    tr: '50 $ üzeri siparişlerde',
  },
  'allProducts.features.securePayment.title': {
    en: 'Secure Payment',
    de: 'Sichere Zahlung',
    tr: 'Güvenli Ödeme',
  },
  'allProducts.features.securePayment.subtitle': {
    en: 'Your data is protected',
    de: 'Ihre Daten sind geschützt',
    tr: 'Verileriniz korunur',
  },
  'allProducts.features.support.title': {
    en: '24/7 Support',
    de: '24/7 Support',
    tr: '7/24 Destek',
  },
  'allProducts.features.support.subtitle': {
    en: "We're here to help",
    de: 'Wir sind für Sie da',
    tr: 'Yardım için buradayız',
  },
  // Product Card
  'productCard.new': { en: 'NEW', de: 'NEU', tr: 'YENİ' },
  'productCard.featured': { en: 'FEATURED', de: 'HERVORGEHOBEN', tr: 'ÖNE ÇIKAN' },
  'productCard.myProduct': { en: 'MY PRODUCT', de: 'MEIN PRODUKT', tr: 'BENİM ÜRÜNÜM' },
  'productCard.addToCart': { en: 'Add to Cart', de: 'In den Warenkorb', tr: 'Sepete Ekle' },
  'productCard.makeOffer': { en: 'Make Offer', de: 'Angebot machen', tr: 'Teklif Ver' },
  'productCard.viewDetails': { en: 'View Details', de: 'Details anzeigen', tr: 'Detayları Gör' },
  'productCard.tooltip.addToFavorites': { en: 'Add to favorites', de: 'Zu Favoriten hinzufügen', tr: 'Favorilere ekle' },
  'productCard.tooltip.removeFromFavorites': { en: 'Remove from favorites', de: 'Aus Favoriten entfernen', tr: 'Favorilerden kaldır' },
  'productCard.tooltip.share': { en: 'Share', de: 'Teilen', tr: 'Paylaş' },
  // Auction Card
  'auctionCard.badge.auction': { en: 'AUCTION', de: 'AUKTION', tr: 'AÇIK ARTIRMA' },
  'auctionCard.badge.new': { en: 'NEW', de: 'NEU', tr: 'YENİ' },
  'auctionCard.badge.myAuction': { en: 'MY AUCTION', de: 'MEINE AUKTION', tr: 'BENİM AÇIK ARTIRMAM' },
  'auctionCard.status.ended': { en: 'Auction Ended', de: 'Auktion beendet', tr: 'Açık artırma bitti' },
  'auctionCard.currentBid': { en: 'Current Bid:', de: 'Aktuelles Gebot:', tr: 'Güncel Teklif:' },
  'auctionCard.reserve': { en: 'Reserve:', de: 'Mindestpreis:', tr: 'Rezerv Fiyatı:' },
  'auctionCard.notSet': { en: 'Not set', de: 'Nicht festgelegt', tr: 'Belirlenmedi' },
  'auctionCard.bids': { en: 'bids', de: 'Gebote', tr: 'teklif' },
  'auctionCard.placeBid': { en: 'Place Bid', de: 'Gebot abgeben', tr: 'Teklif Ver' },
  'auctionCard.viewBids': { en: 'View Bids', de: 'Gebote ansehen', tr: 'Teklifleri Gör' },
  'auctionCard.viewResults': { en: 'View Results', de: 'Ergebnisse ansehen', tr: 'Sonuçları Gör' },
  'auctionCard.viewDetails': { en: 'View Auction Details', de: 'Auktionsdetails anzeigen', tr: 'Açık artırma detaylarını gör' },
  'auctionCard.tooltip.addToFavorites': { en: 'Add to favorites', de: 'Zu Favoriten hinzufügen', tr: 'Favorilere ekle' },
  'auctionCard.tooltip.removeFromFavorites': { en: 'Remove from favorites', de: 'Aus Favoriten entfernen', tr: 'Favorilerden kaldır' },
  'auctionCard.tooltip.share': { en: 'Share', de: 'Teilen', tr: 'Paylaş' },
  // Common extras
  'common.unknownLocation': { en: 'Unknown Location', de: 'Unbekannter Ort', tr: 'Bilinmeyen Konum' },
  // Auto Translation Toggle
  'translation.autoTranslate': {
    en: 'Auto-translate product names & descriptions',
    de: 'Produktnamen & Beschreibungen automatisch übersetzen',
    tr: 'Ürün adları ve açıklamalarını otomatik çevir',
  },
  // Filter Sidebar
  'filterSidebar.search.title.products': { en: 'Search Products', de: 'Produkte suchen', tr: 'Ürün Ara' },
  'filterSidebar.search.title.jobs': { en: 'Search Jobs', de: 'Jobs suchen', tr: 'İş Ara' },
  'filterSidebar.purchaseType.title': { en: 'Purchase Type', de: 'Kaufart', tr: 'Satın Alma Türü' },
  'filterSidebar.purchaseType.all': { en: 'All', de: 'Alle', tr: 'Tümü' },
  'filterSidebar.purchaseType.auctions': { en: 'Auctions', de: 'Auktionen', tr: 'Açık Artırmalar' },
  'filterSidebar.purchaseType.direct': { en: 'Direct Buy', de: 'Direktkauf', tr: 'Doğrudan Satın Al' },
  'filterSidebar.purchaseType.negotiable': { en: 'Negotiable', de: 'Verhandlung', tr: 'Pazarlık' },
  'filterSidebar.purchaseType.offer': { en: 'Offer', de: 'Angebot', tr: 'Teklif' },
  'filterSidebar.aiSearch.title': { en: 'AI Search', de: 'KI-Suche', tr: 'YZ Arama' },
  'filterSidebar.aiSearch.findSimilar': { en: 'Find Similar', de: 'Ähnliche finden', tr: 'Benzerleri Bul' },
  'filterSidebar.map.selectedLocations': { en: 'Selected locations:', de: 'Ausgewählte Orte:', tr: 'Seçilen konumlar:' },
  // Price/Salary range
  'filterSidebar.priceRange.price': { en: 'Price Range', de: 'Preisspanne', tr: 'Fiyat Aralığı' },
  'filterSidebar.priceRange.salary': { en: 'Salary Range', de: 'Gehalts­spanne', tr: 'Maaş Aralığı' },
  'filterSidebar.priceRange.minPrice': { en: 'Min Price', de: 'Mindestpreis', tr: 'En Düşük Fiyat' },
  'filterSidebar.priceRange.maxPrice': { en: 'Max Price', de: 'Höchstpreis', tr: 'En Yüksek Fiyat' },
  'filterSidebar.priceRange.minSalary': { en: 'Min Salary', de: 'Mindestgehalt', tr: 'En Düşük Maaş' },
  'filterSidebar.priceRange.maxSalary': { en: 'Max Salary', de: 'Höchstgehalt', tr: 'En Yüksek Maaş' },
  'filterSidebar.priceRange.minPricePlaceholder': { en: 'e.g. 0', de: 'z. B. 0', tr: 'örn. 0' },
  'filterSidebar.priceRange.maxPricePlaceholder': { en: 'e.g. 2000', de: 'z. B. 2000', tr: 'örn. 2000' },
  'filterSidebar.priceRange.minSalaryPlaceholder': { en: 'e.g. 0', de: 'z. B. 0', tr: 'örn. 0' },
  'filterSidebar.priceRange.maxSalaryPlaceholder': { en: 'e.g. 300000', de: 'z. B. 300000', tr: 'örn. 300000' },
  'filterSidebar.priceRange.quickButtons.under50': { en: 'Under $50', de: 'Unter 50 $', tr: '50 $ Altı' },
  'filterSidebar.priceRange.quickButtons.50to200': { en: '$50–$200', de: '50–200 $', tr: '50–200 $' },
  'filterSidebar.priceRange.quickButtons.200to500': { en: '$200–$500', de: '200–500 $', tr: '200–500 $' },
  'filterSidebar.priceRange.quickButtons.under50k': { en: 'Under $50k', de: 'Unter 50.000 $', tr: '50.000 $ Altı' },
  'filterSidebar.priceRange.quickButtons.50k100k': { en: '$50k–$100k', de: '50.000–100.000 $', tr: '50–100 bin $' },
  'filterSidebar.priceRange.quickButtons.100k200k': { en: '$100k–$200k', de: '100.000–200.000 $', tr: '100–200 bin $' },
  // Salary type title alias
  'filterSidebar.salaryType.title': { en: 'Salary Type', de: 'Gehaltsart', tr: 'Maaş Türü' },
  // Tags
  'filterSidebar.tags.title': { en: 'Tags', de: 'Stichwörter', tr: 'Etiketler' },
  'filterSidebar.tags.placeholder': { en: 'Add a tag and press Enter', de: 'Tag eingeben und Enter drücken', tr: 'Etiket ekleyin ve Enter’a basın' },
  'filterSidebar.tags.selectedTags': { en: 'Selected tags', de: 'Ausgewählte Tags', tr: 'Seçilen etiketler' },
  'filterSidebar.tags.clearAll': { en: 'Clear all', de: 'Alle löschen', tr: 'Tümünü temizle' },
  'filterSidebar.tags.tip': { en: 'Tip: Separate multiple tags with commas', de: 'Tipp: Mehrere Tags mit Komma trennen', tr: 'İpucu: Birden çok etiketi virgülle ayırın' },
  // AI upload hint
  'filterSidebar.aiSearch.uploadImage': { en: 'Upload an image to find visually similar products', de: 'Lade ein Bild hoch, um visuell ähnliche Produkte zu finden', tr: 'Görsel olarak benzer ürünleri bulmak için bir görsel yükleyin' },
  'filterSidebar.category.title.products': { en: 'Category', de: 'Kategorie', tr: 'Kategori' },
  'filterSidebar.category.title.jobs': { en: 'Job Category', de: 'Jobkategorie', tr: 'İş Kategorisi' },
  'filterSidebar.category.allJobCategories': { en: 'All Job Categories', de: 'Alle Jobkategorien', tr: 'Tüm İş Kategorileri' },
  'filterSidebar.brands.title.products': { en: 'Brands', de: 'Marken', tr: 'Markalar' },
  'filterSidebar.brands.title.jobs': { en: 'Companies', de: 'Unternehmen', tr: 'Şirketler' },
  'filterSidebar.brands.selectBrands': { en: 'Select brands', de: 'Marken auswählen', tr: 'Marka seç' },
  'filterSidebar.brands.selectCompanies': { en: 'Select companies', de: 'Unternehmen auswählen', tr: 'Şirket seç' },
  'filterSidebar.brands.brand': { en: 'brand', de: 'Marke', tr: 'marka' },
  'filterSidebar.brands.company': { en: 'company', de: 'Unternehmen', tr: 'şirket' },
  'filterSidebar.brands.selected': { en: 'selected', de: 'ausgewählt', tr: 'seçildi' },
  'filterSidebar.requiredSkills.title': { en: 'Required Skills', de: 'Erforderliche Fähigkeiten', tr: 'Gerekli Yetenekler' },
  'filterSidebar.requiredSkills.placeholder': { en: 'e.g. React, SQL', de: 'z. B. React, SQL', tr: 'örn. React, SQL' },
  'filterSidebar.salaryType.label': { en: 'Salary Type', de: 'Gehaltsart', tr: 'Maaş Türü' },
  'filterSidebar.salaryType.any': { en: 'Any', de: 'Beliebig', tr: 'Farketmez' },
  'filterSidebar.salaryType.hourly': { en: 'Per Hour', de: 'Pro Stunde', tr: 'Saatlik' },
  'filterSidebar.salaryType.fixed': { en: 'Fixed Price', de: 'Festpreis', tr: 'Sabit Fiyat' },
  'filterSidebar.salaryType.negotiable': { en: 'Negotiable', de: 'Verhandelbar', tr: 'Pazarlık' },
  // Condition (Shop)
  'filterSidebar.condition.title': { en: 'Condition', de: 'Zustand', tr: 'Durum' },
  'filterSidebar.condition.all': { en: 'All Conditions', de: 'Alle Zustände', tr: 'Tüm Durumlar' },
  'filterSidebar.condition.new': { en: 'New', de: 'Neu', tr: 'Yeni' },
  'filterSidebar.condition.used': { en: 'Used', de: 'Gebraucht', tr: 'Kullanılmış' },
  'filterSidebar.condition.refurbished': { en: 'Refurbished', de: 'Generalüberholt', tr: 'Yenilenmiş' },
  // Additional Filters
  'filterSidebar.additional.title': { en: 'Additional Filters', de: 'Zusätzliche Filter', tr: 'Ek Filtreler' },
  'filterSidebar.additional.inStockOnly': { en: 'In Stock Only', de: 'Nur vorrätig', tr: 'Sadece Stokta' },
  'filterSidebar.additional.onSaleOnly': { en: 'On Sale Only', de: 'Nur im Angebot', tr: 'Sadece İndirimde' },
  'filterSidebar.additional.excludeMyProducts': { en: 'Exclude My Products', de: 'Eigene Produkte ausschließen', tr: 'Kendi Ürünlerimi Hariç Tut' },
  // Seller
  'filterSidebar.seller.title': { en: 'Seller', de: 'Verkäufer', tr: 'Satıcı' },
  'filterSidebar.seller.searchByAlias': { en: 'Search by seller alias', de: 'Nach Verkäufer-Alias suchen', tr: 'Satıcı takma adına göre ara' },
  // Actions
  'filterSidebar.actions.apply': { en: 'Apply Filters', de: 'Filter anwenden', tr: 'Filtreleri Uygula' },
  'filterSidebar.actions.clear': { en: 'Clear All', de: 'Alle löschen', tr: 'Tümünü Temizle' },
  // Job filters
  'filterSidebar.jobFilters.title': { en: 'Job Filters', de: 'Job-Filter', tr: 'İş Filtreleri' },
  'filterSidebar.jobFilters.description': { en: 'Use the controls above to refine job type, experience, workplace, salary, and skills.', de: 'Verwenden Sie die obigen Einstellungen, um Jobtyp, Erfahrung, Arbeitsplatz, Gehalt und Fähigkeiten zu verfeinern.', tr: 'İş türü, deneyim, çalışma şekli, maaş ve becerileri filtrelemek için yukarıdaki kontrolleri kullanın.' },
  // Job selects: type, experience, workplace
  'filterSidebar.jobs.type.title': { en: 'Job Type', de: 'Beschäftigungsart', tr: 'İş Türü' },
  'filterSidebar.jobs.type.any': { en: 'Any Job Type', de: 'Beliebige Beschäftigungsart', tr: 'Herhangi Bir İş Türü' },
  'filterSidebar.jobs.type.fullTime': { en: 'Full-time', de: 'Vollzeit', tr: 'Tam Zamanlı' },
  'filterSidebar.jobs.type.partTime': { en: 'Part-time', de: 'Teilzeit', tr: 'Yarı Zamanlı' },
  'filterSidebar.jobs.type.contract': { en: 'Contract', de: 'Vertrag', tr: 'Sözleşmeli' },
  'filterSidebar.jobs.type.freelance': { en: 'Freelance', de: 'Freiberuflich', tr: 'Serbest' },
  'filterSidebar.jobs.type.internship': { en: 'Internship', de: 'Praktikum', tr: 'Staj' },
  'filterSidebar.jobs.type.temporary': { en: 'Temporary', de: 'Befristet', tr: 'Geçici' },
  'filterSidebar.jobs.experience.title': { en: 'Experience', de: 'Erfahrung', tr: 'Deneyim' },
  'filterSidebar.jobs.experience.any': { en: 'Any Experience', de: 'Beliebige Erfahrung', tr: 'Herhangi Bir Deneyim' },
  'filterSidebar.jobs.experience.entry': { en: 'Entry-level', de: 'Berufseinstieg', tr: 'Yeni Başlayan' },
  'filterSidebar.jobs.experience.mid': { en: 'Mid-level', de: 'Berufserfahren', tr: 'Orta Seviye' },
  'filterSidebar.jobs.experience.senior': { en: 'Senior-level', de: 'Senior', tr: 'Kıdemli' },
  'filterSidebar.jobs.experience.executive': { en: 'Executive', de: 'Leitung', tr: 'Yönetici' },
  'filterSidebar.jobs.workplace.title': { en: 'Workplace', de: 'Arbeitsort', tr: 'Çalışma Şekli' },
  'filterSidebar.jobs.workplace.any': { en: 'Any Workplace', de: 'Beliebiger Arbeitsort', tr: 'Herhangi Bir Çalışma Şekli' },
  'filterSidebar.jobs.workplace.remote': { en: 'Remote', de: 'Remote', tr: 'Uzaktan' },
  'filterSidebar.jobs.workplace.hybrid': { en: 'Hybrid', de: 'Hybrid', tr: 'Hibrit' },
  'filterSidebar.jobs.workplace.onsite': { en: 'On-site', de: 'Vor Ort', tr: 'Ofisten' },
  // Rating
  'filterSidebar.rating.title': { en: 'Minimum Rating', de: 'Mindestbewertung', tr: 'En Az Puan' },
  'filterSidebar.rating.any': { en: 'Any', de: 'Beliebig', tr: 'Farketmez' },
  // Brands dialog
  'filterSidebar.brands.dialog.selectBrands': { en: 'Select Brands', de: 'Marken wählen', tr: 'Marka Seç' },
  'filterSidebar.brands.dialog.selectCompanies': { en: 'Select Companies', de: 'Unternehmen wählen', tr: 'Şirket Seç' },
  'filterSidebar.brands.dialog.noBrands': { en: 'No brands available', de: 'Keine Marken verfügbar', tr: 'Kullanılabilir marka yok' },
  'filterSidebar.dialog.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'filterSidebar.dialog.done': { en: 'Done', de: 'Fertig', tr: 'Bitti' },
  // User Dashboard - Hero
  'dashboard.hero.settings': { en: 'Settings', de: 'Einstellungen', tr: 'Ayarlar' },
  'dashboard.hero.welcomeBack': { en: 'Welcome back, {name}!', de: 'Willkommen zurück, {name}!', tr: 'Tekrar hoş geldin, {name}!' },
  'dashboard.hero.welcomeCompany': { en: 'Welcome to {company} Dashboard', de: 'Willkommen im {company}-Dashboard', tr: '{company} Paneline Hoş Geldiniz' },
  'dashboard.hero.subtitle.user': { en: 'Manage your orders, favorites, and upload new products', de: 'Verwalte deine Bestellungen, Favoriten und lade neue Produkte hoch', tr: 'Siparişlerini ve favorilerini yönet, yeni ürünler yükle' },
  'dashboard.hero.subtitle.company': { en: "Manage {company}'s products, orders, and company activities", de: 'Verwalte die Produkte, Bestellungen und Aktivitäten von {company}', tr: '{company} ürünlerini, siparişlerini ve şirket aktivitelerini yönet' },
  'dashboard.hero.loadingAlias': { en: 'Loading your alias...', de: 'Alias wird geladen...', tr: 'Takma adın yükleniyor...' },
  'dashboard.hero.yourAlias': { en: 'Your Alias:', de: 'Dein Alias:', tr: 'Takma Adın:' },
  'dashboard.hero.noAlias': { en: 'No alias set', de: 'Kein Alias festgelegt', tr: 'Takma ad ayarlı değil' },
  'dashboard.hero.email': { en: 'Email:', de: 'E-Mail:', tr: 'E-posta:' },
  'dashboard.hero.buttons.messages': { en: 'Messages', de: 'Nachrichten', tr: 'Mesajlar' },
  'dashboard.hero.buttons.profile': { en: 'Your Profile', de: 'Dein Profil', tr: 'Profilin' },
  'dashboard.hero.buttons.myCompanies': { en: 'My Companies', de: 'Meine Unternehmen', tr: 'Şirketlerim' },
  'dashboard.hero.buttons.aiWriter': { en: 'AI Application Writer', de: 'KI-Bewerbungsschreiber', tr: 'YZ Başvuru Yazarı' },
  'dashboard.hero.buttons.aiWriterRestore': { en: 'AI Application Writer • Restore', de: 'KI-Bewerbungsschreiber • Wiederherstellen', tr: 'YZ Başvuru Yazarı • Geri Yükle' },
  'dashboard.hero.buttons.jobShuffle': { en: 'Job Shuffle', de: 'Job-Mix', tr: 'İş Karıştır' },
  // Dashboard Tabs
  'dashboard.tabs.documents': { en: 'Documents', de: 'Dokumente', tr: 'Belgeler' },
  // Dashboard Documents Tab
  'dashboard.documents.title': { en: 'My Documents', de: 'Meine Dokumente', tr: 'Belgelerim' },
  'dashboard.documents.subtitle': { en: 'Upload and manage your personal documents securely', de: 'Lade deine persönlichen Dokumente sicher hoch und verwalte sie', tr: 'Kişisel belgelerinizi güvenle yükleyin ve yönetin' },
  'dashboard.documents.card.title': { en: 'Manage Your Documents', de: 'Dokumente verwalten', tr: 'Belgelerinizi Yönetin' },
  'dashboard.documents.card.description': { en: 'Upload, organize, and access your personal documents in one secure place. Support for PDFs, Word documents, images, and more.', de: 'Lade deine persönlichen Dokumente hoch, organisiere und greife darauf an einem sicheren Ort zu. Unterstützung für PDFs, Word-Dokumente, Bilder und mehr.', tr: 'Belgelerinizi güvenli bir yerde yükleyin, düzenleyin ve erişin. PDF, Word belgeleri, görseller ve daha fazlası desteklenir.' },
  'dashboard.documents.card.openManager': { en: 'Open Documents Manager', de: 'Dokumentenmanager öffnen', tr: 'Belge Yöneticisini Aç' },
  // Dashboard Upload Tab
  'dashboard.tabs.upload.product': { en: 'Upload Product', de: 'Produkt hochladen', tr: 'Ürün Yükle' },
  'dashboard.tabs.upload.job': { en: 'Upload Job', de: 'Job hochladen', tr: 'İş İlanı Yükle' },
  'dashboard.tabs.myProducts': { en: 'My Products', de: 'Meine Produkte', tr: 'Ürünlerim' },
  'dashboard.tabs.jobs': { en: 'Jobs', de: 'Jobs', tr: 'İş İlanları' },
  'dashboard.tabs.favorites': { en: 'Favorites', de: 'Favoriten', tr: 'Favoriler' },
  'dashboard.tabs.auctions': { en: 'Auctions', de: 'Auktionen', tr: 'Açık Artırmalar' },
  'dashboard.tabs.offers': { en: 'Offers', de: 'Angebote', tr: 'Teklifler' },
  'dashboard.tabs.orders': { en: 'Orders', de: 'Bestellungen', tr: 'Siparişler' },
  'dashboard.tabs.interviews': { en: 'Interviews', de: 'Interviews', tr: 'Mülakatlar' },
  'dashboard.tabs.inquiries': { en: 'Inquiries', de: 'Anfragen', tr: 'Sorgular' },
  'dashboard.tabs.talentPool': { en: 'Talent Pool', de: 'Talentpool', tr: 'Yetenek Havuzu' },
  'dashboard.tabs.applications': { en: 'Applications', de: 'Bewerbungen', tr: 'Başvurular' },
  'dashboard.upload.title.product': { en: 'Upload New Product', de: 'Neues Produkt hochladen', tr: 'Yeni Ürün Yükle' },
  'dashboard.upload.title.job': { en: 'Upload New Job', de: 'Neuen Job hochladen', tr: 'Yeni İş İlanı Yükle' },
  'dashboard.upload.subtitle.product': { en: 'Add your own products to sell on FashionHub', de: 'Füge deine eigenen Produkte zum Verkauf auf FashionHub hinzu', tr: 'FashionHub’da satmak için ürünlerini ekle' },
  'dashboard.upload.subtitle.job': { en: 'Post job opportunities and find talented freelancers', de: 'Veröffentliche Jobangebote und finde talentierte Freelancer', tr: 'İş fırsatları yayınla ve yetenekli freelancerlar bul' },
  'dashboard.upload.card.heading.product': { en: 'Ready to start selling?', de: 'Bereit mit dem Verkaufen zu beginnen?', tr: 'Satışa başlamak için hazır mısın?' },
  'dashboard.upload.card.heading.job': { en: 'Ready to start hiring?', de: 'Bereit mit dem Einstellen zu beginnen?', tr: 'İşe alıma başlamak için hazır mısın?' },
  'dashboard.upload.card.description.product': { en: 'Upload your first product and start your fashion business today!', de: 'Lade dein erstes Produkt hoch und starte heute dein Fashion-Business!', tr: 'İlk ürününü yükle ve bugün moda işine başla!' },
  'dashboard.upload.card.description.job': { en: 'Post your first job and start building your team today!', de: 'Veröffentliche deinen ersten Job und baue noch heute dein Team auf!', tr: 'İlk iş ilanını paylaş ve bugün ekibini kurmaya başla!' },
  'dashboard.upload.button.product': { en: 'Upload Product', de: 'Produkt hochladen', tr: 'Ürün Yükle' },
  'dashboard.upload.button.job': { en: 'Upload Job', de: 'Job hochladen', tr: 'İş İlanı Yükle' },
  // Dashboard My Products Tab

  // Product Upload Page
  // Selling Method (Step 0)
  'productUpload.sellingMethod.title': { en: 'Choose Your Selling Method', de: 'Wähle deine Verkaufsmethode', tr: 'Satış Yöntemini Seç' },
  'productUpload.sellingMethod.subtitle': { en: 'Select how you want to sell your product - direct purchase or auction', de: 'Wähle, wie du dein Produkt verkaufen möchtest – Direktkauf oder Auktion', tr: 'Ürününüzü nasıl satmak istediğinizi seçin – doğrudan satış veya açık artırma' },
  'productUpload.sellingMethod.direct.title': { en: 'Direct Buy', de: 'Direktkauf', tr: 'Doğrudan Satın Al' },
  'productUpload.sellingMethod.direct.desc': { en: 'Set a fixed price and sell immediately when customers purchase', de: 'Lege einen Festpreis fest und verkaufe sofort, wenn Kunden kaufen', tr: 'Sabit bir fiyat belirleyin ve müşteriler satın aldığında hemen satın' },
  'productUpload.sellingMethod.direct.bullets.instantSales': { en: 'Instant sales', de: 'Sofortige Verkäufe', tr: 'Anında satış' },
  'productUpload.sellingMethod.direct.bullets.fixedPricing': { en: 'Fixed pricing', de: 'Feste Preisgestaltung', tr: 'Sabit fiyatlandırma' },
  'productUpload.sellingMethod.direct.bullets.predictableRevenue': { en: 'Predictable revenue', de: 'Planbare Einnahmen', tr: 'Öngörülebilir gelir' },
  'productUpload.sellingMethod.direct.bullets.inventoryManagement': { en: 'Inventory management', de: 'Bestandsverwaltung', tr: 'Stok yönetimi' },
  'productUpload.sellingMethod.auction.title': { en: 'Auction', de: 'Auktion', tr: 'Açık Artırma' },
  'productUpload.sellingMethod.auction.desc': { en: 'Let buyers bid on your product and sell to the highest bidder', de: 'Lass Käufer auf dein Produkt bieten und verkaufe an den Höchstbietenden', tr: 'Alıcıların ürününüze teklif vermesine izin verin ve en yüksek teklif verene satın' },
  'productUpload.sellingMethod.auction.bullets.competitiveBidding': { en: 'Competitive bidding', de: 'Wettbewerbliches Bieten', tr: 'Rekabetçi teklif' },
  'productUpload.sellingMethod.auction.bullets.higherValue': { en: 'Higher potential value', de: 'Höherer potenzieller Wert', tr: 'Daha yüksek potansiyel değer' },
  'productUpload.sellingMethod.auction.bullets.timeLimited': { en: 'Time-limited sales', de: 'Zeitlich begrenzte Verkäufe', tr: 'Süreli satış' },
  'productUpload.sellingMethod.auction.bullets.marketDiscovery': { en: 'Market discovery', de: 'Markterkundung', tr: 'Pazar keşfi' },
  
  // Step 2: Basic Information
  'productUpload.basic.title': { en: 'Basic Information', de: 'Basisinformationen', tr: 'Temel Bilgiler' },
  'productUpload.basic.autofill': { en: '🎲 Autofill Sample Data', de: '🎲 Beispieldaten ausfüllen', tr: '🎲 Örnek Verileri Doldur' },
  'productUpload.basic.name.label': { en: 'Product Name *', de: 'Produktname *', tr: 'Ürün Adı *' },
  'productUpload.basic.name.placeholder': { en: 'Enter a descriptive product name', de: 'Gib einen aussagekräftigen Produktnamen ein', tr: 'Açıklayıcı bir ürün adı girin' },
  'productUpload.basic.description.label': { en: 'Description *', de: 'Beschreibung *', tr: 'Açıklama *' },
  'productUpload.basic.description.placeholder': { en: 'Describe your product in detail...', de: 'Beschreibe dein Produkt ausführlich...', tr: 'Ürününüzü ayrıntılı olarak açıklayın...' },
  'productUpload.basic.brand.label': { en: 'Brand *', de: 'Marke *', tr: 'Marka *' },
  'productUpload.basic.brand.placeholder': { en: 'Enter brand name', de: 'Markennamen eingeben', tr: 'Marka adını girin' },
  'productUpload.basic.category.label': { en: 'Category *', de: 'Kategorie *', tr: 'Kategori *' },
  'productUpload.basic.categories.errorLoading': { en: 'Error loading categories', de: 'Fehler beim Laden der Kategorien', tr: 'Kategoriler yüklenirken hata oluştu' },
  'productUpload.basic.categories.retry': { en: 'Retry', de: 'Wiederholen', tr: 'Tekrar Dene' },
  'productUpload.basic.categories.none': { en: 'No categories available', de: 'Keine Kategorien verfügbar', tr: 'Kategori yok' },
  'productUpload.basic.subcategory.label': { en: 'Subcategory *', de: 'Unterkategorie *', tr: 'Alt Kategori *' },
  'productUpload.basic.subcategories.selectCategoryFirst': { en: 'Please select a category first', de: 'Bitte wähle zuerst eine Kategorie', tr: 'Lütfen önce bir kategori seçin' },
  'productUpload.basic.subcategories.errorLoading': { en: 'Error loading subcategories', de: 'Fehler beim Laden der Unterkategorien', tr: 'Alt kategoriler yüklenirken hata oluştu' },
  'productUpload.basic.subcategories.retry': { en: 'Retry', de: 'Wiederholen', tr: 'Tekrar Dene' },
  'productUpload.basic.subcategories.noneForCategory': { en: 'No subcategories available for this category', de: 'Für diese Kategorie sind keine Unterkategorien verfügbar', tr: 'Bu kategori için alt kategori yok' },

  // Step 3: Pricing & Details
  'productUpload.details.title': { en: 'Pricing & Details', de: 'Preis & Details', tr: 'Fiyatlandırma & Detaylar' },
  'productUpload.details.autofill': { en: '🎲 Autofill Sample Data', de: '🎲 Beispieldaten ausfüllen', tr: '🎲 Örnek Verileri Doldur' },
  'productUpload.details.auctionInfo': { en: '💡 All pricing information is configured in the Auction Settings section below', de: '💡 Alle Preisangaben werden unten im Abschnitt Auktions-Einstellungen konfiguriert', tr: '💡 Tüm fiyat bilgileri aşağıdaki Açık Artırma Ayarları bölümünde yapılandırılır' },

  // Pricing mode
  'productUpload.details.pricingMode.title': { en: 'Pricing Mode', de: 'Preismodus', tr: 'Fiyatlandırma Modu' },
  'productUpload.details.pricingMode.setCustomer': { en: 'Set Customer Price', de: 'Kundenpreis festlegen', tr: 'Müşteri Fiyatı Belirle' },
  'productUpload.details.pricingMode.setBase': { en: 'Set Base Price', de: 'Basispreis festlegen', tr: 'Taban Fiyatı Belirle' },
  'productUpload.details.pricingMode.helpCustomer': { en: 'Enter the price customers will see', de: 'Preis eingeben, den Kunden sehen', tr: 'Müşterilerin göreceği fiyatı girin' },
  'productUpload.details.pricingMode.helpBase': { en: 'Enter your base selling price', de: 'Deinen Basisverkaufspreis eingeben', tr: 'Taban satış fiyatınızı girin' },

  // Fees panel
  'productUpload.details.fees.platformFee': { en: 'Platform Fee:', de: 'Plattformgebühr:', tr: 'Platform Ücreti:' },
  'productUpload.details.fees.fixedPerSale': { en: 'fixed per sale', de: 'pro Verkauf (fix)', tr: 'satış başına sabit' },
  'productUpload.details.fees.ofSalePrice': { en: 'of sale price', de: 'des Verkaufspreises', tr: 'satış fiyatının' },
  'productUpload.details.fees.customerPrice': { en: 'Customer Price:', de: 'Kundenpreis:', tr: 'Müşteri Fiyatı:' },
  'productUpload.details.fees.basePrice': { en: 'Base Price:', de: 'Basispreis:', tr: 'Taban Fiyat:' },
  'productUpload.details.fees.youWillReceive': { en: 'You Will Receive:', de: 'Du erhältst:', tr: 'Elde Edeceğiniz Tutar:' },
  'productUpload.details.fees.customerWillPay': { en: 'Customer Will Pay:', de: 'Kunde zahlt:', tr: 'Müşteri Ödeyecek:' },

  // Price fields
  'productUpload.details.fields.customerPrice': { en: 'Customer Price *', de: 'Kundenpreis *', tr: 'Müşteri Fiyatı *' },
  'productUpload.details.fields.basePrice': { en: 'Base Price *', de: 'Basispreis *', tr: 'Taban Fiyat *' },
  'productUpload.details.fields.customerPriceHelp': { en: 'The price customers will see (includes platform fees)', de: 'Preis, den Kunden sehen (inkl. Plattformgebühren)', tr: 'Müşterilerin göreceği fiyat (platform ücretleri dahil)' },
  'productUpload.details.fields.basePriceHelp': { en: 'Your selling price (platform fees will be added)', de: 'Dein Verkaufspreis (Plattformgebühren werden addiert)', tr: 'Satış fiyatınız (platform ücretleri eklenecek)' },
  'productUpload.details.fields.originalPrice': { en: 'Original Price (Optional)', de: 'Originalpreis (optional)', tr: 'Orijinal Fiyat (İsteğe Bağlı)' },
  'productUpload.details.fields.originalPriceHelp': { en: 'For showing discounts', de: 'Zur Anzeige von Rabatten', tr: 'İndirim göstermek için' },
  'productUpload.details.quickSuggestions': { en: '💡 Quick Price Suggestions', de: '💡 Schnelle Preisvorschläge', tr: '💡 Hızlı Fiyat Önerileri' },
  'productUpload.details.quickSuggestionYouGet': { en: 'You get', de: 'Du erhältst', tr: 'Elinize geçecek' },
  'productUpload.details.quickSuggestionItem': { en: '{customer} → You get {base}', de: '{customer} → Du erhältst {base}', tr: '{customer} → Elinize geçecek {base}' },

  // Auction section
  'productUpload.details.auction.title': { en: 'Auction Settings', de: 'Auktions-Einstellungen', tr: 'Açık Artırma Ayarları' },
  'productUpload.details.auction.subtitle': { en: 'Configure your auction parameters to maximize bidding potential', de: 'Konfiguriere deine Auktionsparameter, um das Gebotspotenzial zu maximieren', tr: 'Teklif potansiyelini artırmak için açık artırma parametrelerinizi yapılandırın' },
  'productUpload.details.auction.minBid': { en: 'Minimum Bid Price *', de: 'Mindestgebotspreis *', tr: 'Minimum Teklif Fiyatı *' },
  'productUpload.details.auction.minBidHelp': { en: 'Starting price for bidding - this is where the auction begins', de: 'Startpreis für Gebote – hier beginnt die Auktion', tr: 'Teklif başlangıç fiyatı – açık artırma burada başlar' },
  'productUpload.details.auction.reserve': { en: 'Reserve Price (Optional)', de: 'Mindestpreis (optional)', tr: 'Rezerv Fiyatı (İsteğe Bağlı)' },
  'productUpload.details.auction.reserveHelp': { en: 'Minimum price to sell (hidden from bidders)', de: 'Mindestverkaufspreis (für Bieter verborgen)', tr: 'Satış için minimum fiyat (teklif verenlerden gizli)' },
  'productUpload.details.auction.increment': { en: 'Bid Increment *', de: 'Gebotsschritt *', tr: 'Teklif Artışı *' },
  'productUpload.details.auction.incrementHelp': { en: 'Minimum bid increase amount', de: 'Mindestbetrag der Gebotserhöhung', tr: 'Minimum teklif artış miktarı' },
  'productUpload.details.auction.endTime': { en: 'Auction End Date & Time *', de: 'Auktionsende (Datum & Uhrzeit) *', tr: 'Açık Artırma Bitiş Tarihi ve Saati *' },
  'productUpload.details.auction.endTimeHelp': { en: 'When the auction will end - choose a time that works for your target audience', de: 'Wann die Auktion endet – wähle eine für deine Zielgruppe passende Zeit', tr: 'Açık artırmanın biteceği zaman – hedef kitlenize uygun bir saat seçin' },

  // Other fields
  'productUpload.details.condition.label': { en: 'Condition', de: 'Zustand', tr: 'Durum' },
  'productUpload.details.condition.new': { en: 'New', de: 'Neu', tr: 'Yeni' },
  'productUpload.details.condition.used': { en: 'Used', de: 'Gebraucht', tr: 'Kullanılmış' },
  'productUpload.details.condition.refurbished': { en: 'Refurbished', de: 'Generalüberholt', tr: 'Yenilenmiş' },
  'productUpload.details.stock': { en: 'Stock Quantity', de: 'Lagerbestand', tr: 'Stok Miktarı' },
  'productUpload.details.material': { en: 'Material', de: 'Material', tr: 'Malzeme' },

  // Colors
  'productUpload.details.colors.title': { en: 'Available Colors', de: 'Verfügbare Farben', tr: 'Mevcut Renkler' },
  'productUpload.details.colors.standard': { en: 'Standard Colors', de: 'Standardfarben', tr: 'Standart Renkler' },
  'productUpload.details.colors.addStandard': { en: 'Add Standard Color', de: 'Standardfarbe hinzufügen', tr: 'Standart Renk Ekle' },
  'productUpload.details.colors.custom': { en: 'Custom Colors', de: 'Benutzerdefinierte Farben', tr: 'Özel Renkler' },
  'productUpload.details.colors.addCustom': { en: 'Add Custom Color', de: 'Benutzerdefinierte Farbe hinzufügen', tr: 'Özel Renk Ekle' },
  'productUpload.details.colors.selectCustom': { en: 'Select Custom Color', de: 'Benutzerdefinierte Farbe wählen', tr: 'Özel Renk Seç' },
  'productUpload.details.colors.add': { en: 'Add Color', de: 'Farbe hinzufügen', tr: 'Renk Ekle' },
  'productUpload.details.common.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },

  // Sizes
  'productUpload.details.sizes.title': { en: 'Size System', de: 'Größensystem', tr: 'Beden Sistemi' },
  'productUpload.details.sizes.selectSystem': { en: 'Select Size System *', de: 'Größensystem auswählen *', tr: 'Beden Sistemini Seçin *' },
  'productUpload.details.sizes.available': { en: 'Available Sizes', de: 'Verfügbare Größen', tr: 'Mevcut Bedenler' },
  'productUpload.details.sizes.add': { en: 'Add Size', de: 'Größe hinzufügen', tr: 'Beden Ekle' },

  // Tags
  'productUpload.details.tags.title': { en: 'Tags', de: 'Tags', tr: 'Etiketler' },
  'productUpload.details.tags.add': { en: 'Add Tag', de: 'Tag hinzufügen', tr: 'Etiket Ekle' },
  'productUpload.details.tags.placeholder': { en: 'Press Enter to add tag', de: 'Drücke Enter, um ein Tag hinzuzufügen', tr: 'Etiket eklemek için Enter’a basın' },

  // Step 4: Images
  'productUpload.images.title': { en: 'Product Images', de: 'Produktbilder', tr: 'Ürün Görselleri' },
  'productUpload.images.autofill': { en: '🎲 Autofill Sample Images', de: '🎲 Beispielbilder einfügen', tr: '🎲 Örnek Görselleri Doldur' },
  'productUpload.images.info.line1': { en: 'Add high-quality images to showcase your product. You can take a photo with your camera, upload files from your device, or paste image URLs.', de: 'Füge hochwertige Bilder hinzu, um dein Produkt zu präsentieren. Du kannst mit deiner Kamera ein Foto machen, Dateien von deinem Gerät hochladen oder Bild-URLs einfügen.', tr: 'Ürününüzü sergilemek için yüksek kaliteli görseller ekleyin. Kameranızla fotoğraf çekebilir, cihazınızdan dosya yükleyebilir veya görsel URL’leri yapıştırabilirsiniz.' },
  'productUpload.images.info.line2': { en: 'Supported formats: JPG, PNG, GIF, WebP. Maximum file size: 5MB per image.', de: 'Unterstützte Formate: JPG, PNG, GIF, WebP. Maximale Dateigröße: 5 MB pro Bild.', tr: 'Desteklenen formatlar: JPG, PNG, GIF, WebP. Maksimum dosya boyutu: görsel başına 5 MB.' },
  'productUpload.images.addTitle': { en: 'Add Product Images', de: 'Produktbilder hinzufügen', tr: 'Ürün Görselleri Ekle' },
  'productUpload.images.addSubtitle': { en: 'Upload files or paste image URLs', de: 'Dateien hochladen oder Bild-URLs einfügen', tr: 'Dosya yükleyin veya görsel URL’leri yapıştırın' },
  'productUpload.images.urlLabel': { en: 'Image URL', de: 'Bild-URL', tr: 'Görsel URL’si' },
  'productUpload.images.urlPlaceholder': { en: 'https://example.com/image.jpg', de: 'https://beispiel.de/bild.jpg', tr: 'https://ornek.com/gorsel.jpg' },
  'productUpload.images.addImageBtn': { en: 'Add Image', de: 'Bild hinzufügen', tr: 'Görsel Ekle' },
  'productUpload.images.uploading': { en: 'Uploading...', de: 'Wird hochgeladen...', tr: 'Yükleniyor...' },
  'productUpload.images.uploadFromDevice': { en: 'Upload from Device', de: 'Vom Gerät hochladen', tr: 'Cihazdan Yükle' },
  'productUpload.images.takePhoto': { en: 'Take Photo', de: 'Foto aufnehmen', tr: 'Foto Çek' },
  'productUpload.images.selectFromDocuments': { en: 'Select from Documents', de: 'Aus Dokumenten auswählen', tr: 'Belgelerden Seç' },
  'productUpload.images.previewTitle': { en: 'Preview Images', de: 'Bildervorschau', tr: 'Önizleme Görselleri' },
  'productUpload.images.altPrefix': { en: 'Product', de: 'Produkt', tr: 'Ürün' },

  // Step 5: Shipping
  'productUpload.shipping.title': { en: 'Shipping Options (Germany)', de: 'Versandoptionen (Deutschland)', tr: 'Kargo Seçenekleri (Almanya)' },
  'productUpload.shipping.selectedSuffix': { en: 'selected', de: 'ausgewählt', tr: 'seçildi' },
  'productUpload.shipping.loading': { en: 'Loading delivery options…', de: 'Versandoptionen werden geladen…', tr: 'Teslimat seçenekleri yükleniyor…' },
  'productUpload.shipping.noneForCarrierPrefix': { en: 'No options for', de: 'Keine Optionen für', tr: 'Seçenek yok:' },
  'productUpload.shipping.card.max': { en: 'max', de: 'max.', tr: 'maks.' },
  'productUpload.shipping.card.weightLimitsVary': { en: 'weight limits vary', de: 'Gewichtsbeschränkungen variieren', tr: 'ağırlık sınırları değişir' },
  'productUpload.shipping.card.sizeUpTo': { en: 'Size up to', de: 'Größe bis', tr: 'Boyut üst sınırı' },
  'productUpload.shipping.card.cm': { en: 'cm', de: 'cm', tr: 'cm' },
  'productUpload.shipping.selectedTitle': { en: 'Selected', de: 'Ausgewählt', tr: 'Seçilenler' },
  'productUpload.shipping.helper': { en: 'Choose a carrier above, then select one or more tiers. You can combine carriers.', de: 'Wähle oben einen Versanddienstleister und dann eine oder mehrere Stufen. Du kannst Dienstleister kombinieren.', tr: 'Yukarıdan bir kargo firmasını seçin, sonra bir veya daha fazla seçenek işaretleyin. Birden fazla firma birleştirilebilir.' },

  // Step 6: Location
  'productUpload.location.title': { en: 'Select Location', de: 'Standort auswählen', tr: 'Konum Seç' },
  'productUpload.location.badge.selected': { en: 'Location Selected', de: 'Standort ausgewählt', tr: 'Konum Seçildi' },
  'productUpload.location.badge.mapSelected': { en: 'Map Location Selected', de: 'Kartenstandort ausgewählt', tr: 'Harita Konumu Seçildi' },
  'productUpload.location.info': { en: 'Choose the address where this product will be shipped from. This will be used for shipping calculations and buyer information.', de: 'Wähle die Adresse, von der dieses Produkt versendet wird. Diese wird für Versandberechnungen und Käuferinformationen verwendet.', tr: 'Bu ürünün gönderileceği adresi seçin. Bu bilgi kargo hesaplamaları ve alıcı bilgileri için kullanılacaktır.' },
  'productUpload.location.loading': { en: 'Loading your addresses...', de: 'Deine Adressen werden geladen...', tr: 'Adresleriniz yükleniyor...' },
  'productUpload.location.empty.title': { en: 'No addresses found', de: 'Keine Adressen gefunden', tr: 'Adres bulunamadı' },
  'productUpload.location.empty.subtitle': { en: 'You need to add at least one address to your account before uploading products.', de: 'Du musst mindestens eine Adresse zu deinem Konto hinzufügen, bevor du Produkte hochlädst.', tr: 'Ürün yüklemeden önce hesabınıza en az bir adres eklemelisiniz.' },
  'productUpload.location.empty.action': { en: 'Add Address in Settings', de: 'Adresse in den Einstellungen hinzufügen', tr: 'Ayarlar’dan Adres Ekle' },
  'productUpload.location.default': { en: 'Default', de: 'Standard', tr: 'Varsayılan' },
  'productUpload.location.viewOnMap': { en: 'View on Map', de: 'Auf Karte anzeigen', tr: 'Haritada Görüntüle' },
  'productUpload.location.selectedTitle': { en: 'Selected Location', de: 'Ausgewählter Standort', tr: 'Seçilen Konum' },
  'productUpload.location.mapSelected.title': { en: 'Map Selected Location', de: 'Auf Karte ausgewählter Standort', tr: 'Haritada Seçilen Konum' },
  'productUpload.location.mapSelected.badge': { en: 'Map Location', de: 'Kartenstandort', tr: 'Harita Konumu' },
  'productUpload.location.mapSelected.subtitle': { en: 'Selected from map search', de: 'Aus Kartensuche ausgewählt', tr: 'Harita aramasından seçildi' },
  'productUpload.location.coordinates': { en: 'Coordinates:', de: 'Koordinaten:', tr: 'Koordinatlar:' },
  'productUpload.location.addToAddresses': { en: 'Add to Addresses', de: 'Zu Adressen hinzufügen', tr: 'Adreslere Ekle' },
  'productUpload.location.addToAddressesRequired': { en: '⚠️ Add to Addresses (Required)', de: '⚠️ Zu Adressen hinzufügen (Erforderlich)', tr: '⚠️ Adreslere Ekle (Zorunlu)' },
  'productUpload.steps.productType': { en: 'Product Type', de: 'Produkttyp', tr: 'Ürün Türü' },
  'productUpload.steps.basicInfo': { en: 'Basic Info', de: 'Basisinformationen', tr: 'Temel Bilgiler' },
  'productUpload.steps.details': { en: 'Details', de: 'Details', tr: 'Detaylar' },
  'productUpload.steps.images': { en: 'Images', de: 'Bilder', tr: 'Görseller' },
  'productUpload.steps.shipping': { en: 'Shipping', de: 'Versand', tr: 'Kargo' },
  'productUpload.steps.location': { en: 'Location', de: 'Standort', tr: 'Konum' },
  'productUpload.steps.reviewPublish': { en: 'Review & Publish', de: 'Überprüfen & Veröffentlichen', tr: 'Gözden Geçir & Yayınla' },

  'productUpload.header.title': { en: 'Upload New Product', de: 'Neues Produkt hochladen', tr: 'Yeni Ürün Yükle' },
  'productUpload.header.subtitle': { en: 'Add your own products to sell on FashionHub', de: 'Füge deine eigenen Produkte zum Verkauf auf FashionHub hinzu', tr: 'FashionHub’da satmak için ürünlerini ekle' },

  'productUpload.nav.back': { en: 'Back', de: 'Zurück', tr: 'Geri' },
  'productUpload.nav.next': { en: 'Next', de: 'Weiter', tr: 'İleri' },
  'productUpload.nav.addAddressFirst': { en: '⚠️ Add Address First', de: '⚠️ Zuerst Adresse hinzufügen', tr: '⚠️ Önce Adres Ekle' },

  'productUpload.buttons.testValidation': { en: '🧪 Test Validation', de: '🧪 Validierung testen', tr: '🧪 Doğrulamayı Test Et' },
  'productUpload.buttons.publish': { en: 'Publish Product', de: 'Produkt veröffentlichen', tr: 'Ürünü Yayınla' },
  'productUpload.buttons.publishing': { en: 'Publishing...', de: 'Veröffentlichen...', tr: 'Yayınlanıyor...' },

  'productUpload.validation.passed': { en: 'Step validation passed for step', de: 'Schrittvalidierung bestanden für Schritt', tr: 'Adım doğrulaması geçti, adım' },
  'productUpload.validation.failed': { en: 'Step validation failed for step', de: 'Schrittvalidierung fehlgeschlagen für Schritt', tr: 'Adım doğrulaması başarısız, adım' },

  'productUpload.toast.alreadyOnStep': { en: 'You are already on step', de: 'Du befindest dich bereits in Schritt', tr: 'Zaten bu adımdasın' },
  'productUpload.toast.navigatingToStep': { en: 'Navigating to step', de: 'Wechsel zu Schritt', tr: 'Adıma geçiliyor' },
  'productUpload.toast.stepCompleted': { en: 'Step completed!', de: 'Schritt abgeschlossen!', tr: 'Adım tamamlandı!' },
  'productUpload.common.previousStep': { en: 'Previous step', de: 'Vorheriger Schritt', tr: 'Önceki adım' },

  // Crop Dialog
  'productUpload.crop.title': { en: 'Crop Image', de: 'Bild zuschneiden', tr: 'Görüntüyü Kırp' },
  'productUpload.crop.zoomOut': { en: 'Zoom Out', de: 'Verkleinern', tr: 'Uzaklaştır' },
  'productUpload.crop.zoomIn': { en: 'Zoom In', de: 'Vergrößern', tr: 'Yakınlaştır' },
  'productUpload.crop.rotateLeft': { en: 'Rotate Left', de: 'Nach links drehen', tr: 'Sola Döndür' },
  'productUpload.crop.rotateRight': { en: 'Rotate Right', de: 'Nach rechts drehen', tr: 'Sağa Döndür' },
  'productUpload.crop.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'productUpload.crop.apply': { en: 'Apply Crop', de: 'Zuschnitt anwenden', tr: 'Kırpmayı Uygula' },
  'productUpload.crop.uploading': { en: 'Uploading...', de: 'Hochladen...', tr: 'Yükleniyor...' },

  // Camera Dialog
  'productUpload.camera.title': { en: 'Take Photo', de: 'Foto machen', tr: 'Foto Çek' },
  'productUpload.camera.starting': { en: 'Starting camera...', de: 'Kamera wird gestartet...', tr: 'Kamera başlatılıyor...' },
  'productUpload.camera.switch': { en: 'Switch Camera', de: 'Kamera wechseln', tr: 'Kamerayı Değiştir' },
  'productUpload.camera.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'productUpload.camera.capture': { en: 'Capture', de: 'Aufnehmen', tr: 'Çek' },

  // Gallery
  'productUpload.gallery.title': { en: 'Product Images', de: 'Produktbilder', tr: 'Ürün Görselleri' },

  // Success Modal
  'productUpload.success.title': { en: 'Product Uploaded Successfully!', de: 'Produkt erfolgreich hochgeladen!', tr: 'Ürün Başarıyla Yüklendi!' },
  'productUpload.success.description': { en: 'Your product "{name}" has been successfully uploaded to the marketplace.', de: 'Dein Produkt "{name}" wurde erfolgreich in den Marktplatz hochgeladen.', tr: '"{name}" adlı ürününüz başarıyla pazara yüklendi.' },
  'productUpload.success.close': { en: 'Close', de: 'Schließen', tr: 'Kapat' },
  'productUpload.success.viewDashboard': { en: 'View Dashboard', de: 'Zum Dashboard', tr: 'Paneli Görüntüle' },

  // Review section
  'productUpload.review.productSummary': { en: 'Product Summary', de: 'Produktübersicht', tr: 'Ürün Özeti' },
  'productUpload.review.auctionDetails': { en: 'Auction Details', de: 'Auktionsdetails', tr: 'Açık Artırma Detayları' },
  'productUpload.review.selectedColors': { en: 'Selected Colors', de: 'Ausgewählte Farben', tr: 'Seçilen Renkler' },
  'productUpload.review.noColors': { en: 'No colors selected', de: 'Keine Farben ausgewählt', tr: 'Renk seçilmedi' },
  'productUpload.review.selectedSizes': { en: 'Selected Sizes', de: 'Ausgewählte Größen', tr: 'Seçilen Bedenler' },
  'productUpload.review.noSizes': { en: 'No sizes selected', de: 'Keine Größen ausgewählt', tr: 'Beden seçilmedi' },
  'productUpload.review.selectedShipping': { en: 'Selected Shipping Options', de: 'Ausgewählte Versandoptionen', tr: 'Seçilen Kargo Seçenekleri' },
  'productUpload.review.noShipping': { en: 'No shipping options selected', de: 'Keine Versandoptionen ausgewählt', tr: 'Kargo seçeneği seçilmedi' },
  'productUpload.review.selectedLocation': { en: 'Selected Location', de: 'Ausgewählter Standort', tr: 'Seçilen Konum' },
  'productUpload.review.addressNotFound': { en: 'Address not found', de: 'Adresse nicht gefunden', tr: 'Adres bulunamadı' },
  'productUpload.review.noLocation': { en: 'No location selected', de: 'Kein Standort ausgewählt', tr: 'Konum seçilmedi' },
  'productUpload.review.productTags': { en: 'Product Tags', de: 'Produkt-Tags', tr: 'Ürün Etiketleri' },
  'productUpload.review.publishNow': { en: 'Publish immediately', de: 'Sofort veröffentlichen', tr: 'Hemen yayınla' },
  'productUpload.review.publishNow.desc': { en: 'Make product visible to buyers right away', de: 'Produkt sofort für Käufer sichtbar machen', tr: 'Ürünü hemen alıcılara görünür yap' },

  // Common small labels used in review cards
  'productUpload.labels.name': { en: 'Name:', de: 'Name:', tr: 'Ad:' },
  'productUpload.labels.category': { en: 'Category:', de: 'Kategorie:', tr: 'Kategori:' },
  'productUpload.labels.subcategory': { en: 'Subcategory:', de: 'Unterkategorie:', tr: 'Alt kategori:' },
  'productUpload.labels.price': { en: 'Price:', de: 'Preis:', tr: 'Fiyat:' },
  'productUpload.labels.stock': { en: 'Stock:', de: 'Lager:', tr: 'Stok:' },
  'productUpload.labels.brand': { en: 'Brand:', de: 'Marke:', tr: 'Marka:' },
  'productUpload.labels.sizeSystem': { en: 'Size System:', de: 'Größensystem:', tr: 'Beden Sistemi:' },
  'productUpload.labels.images': { en: 'Images:', de: 'Bilder:', tr: 'Görseller:' },
  'productUpload.labels.material': { en: 'Material:', de: 'Material:', tr: 'Malzeme:' },
  'productUpload.labels.condition': { en: 'Condition:', de: 'Zustand:', tr: 'Durum:' },
  'productUpload.labels.units': { en: 'units', de: 'Stück', tr: 'adet' },
  'productUpload.labels.notSet': { en: 'Not set', de: 'Nicht festgelegt', tr: 'Belirlenmedi' },
  'productUpload.labels.days': { en: 'days', de: 'Tage', tr: 'gün' },
  'productUpload.labels.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'productUpload.labels.ended': { en: 'Ended', de: 'Beendet', tr: 'Bitti' },
  'productUpload.labels.noReserve': { en: 'No reserve', de: 'Kein Mindestpreis', tr: 'Rezerv yok' },
  'productUpload.labels.carrier': { en: 'Carrier:', de: 'Versanddienst:', tr: 'Kargo firması:' },
  'productUpload.labels.country': { en: 'Country:', de: 'Land:', tr: 'Ülke:' },
  'productUpload.labels.maxWeight': { en: 'Max weight:', de: 'Max. Gewicht:', tr: 'Azami ağırlık:' },
  'productUpload.labels.tracking': { en: 'Tracking:', de: 'Sendungsverfolgung:', tr: 'Takip:' },
  'productUpload.labels.yes': { en: 'Yes', de: 'Ja', tr: 'Evet' },
  'productUpload.labels.no': { en: 'No', de: 'Nein', tr: 'Hayır' },
  'productUpload.labels.unknownStep': { en: 'Unknown step', de: 'Unbekannter Schritt', tr: 'Bilinmeyen adım' },

  // Product type labels
  'productUpload.productType.direct': { en: 'Direct Buy Product', de: 'Direktkauf-Produkt', tr: 'Doğrudan Satış Ürünü' },
  'productUpload.productType.direct.desc': { en: 'Buyers can purchase immediately at fixed price', de: 'Käufer können sofort zum Festpreis kaufen', tr: 'Alıcılar sabit fiyattan hemen satın alabilir' },
  'productUpload.productType.auction': { en: 'Auction Product', de: 'Auktionsprodukt', tr: 'Açık Artırma Ürünü' },
  'productUpload.productType.auction.desc': { en: 'Buyers will bid on this product', de: 'Käufer werden auf dieses Produkt bieten', tr: 'Alıcılar bu ürüne teklif verecek' },

  // Auction labels
  'productUpload.auction.startPrice': { en: 'Starting Price:', de: 'Startpreis:', tr: 'Başlangıç Fiyatı:' },
  'productUpload.auction.bidIncrement': { en: 'Bid Increment:', de: 'Gebotsschritt:', tr: 'Teklif Artışı:' },
  'productUpload.auction.end': { en: 'Auction End:', de: 'Auktionsende:', tr: 'Açık Artırma Bitişi:' },
  'productUpload.auction.reserve': { en: 'Reserve Price:', de: 'Mindestpreis:', tr: 'Rezerv Fiyatı:' },
  'productUpload.auction.duration': { en: 'Duration:', de: 'Dauer:', tr: 'Süre:' },
  'productUpload.auction.status': { en: 'Status:', de: 'Status:', tr: 'Durum:' },
  'productUpload.labels.minBidPrice': { en: 'Minimum Bid Price:', de: 'Mindestgebot:', tr: 'Minimum Teklif Fiyatı:' },

  // Success description parts
  'productUpload.success.descriptionPrefix': { en: 'Your product', de: 'Dein Produkt', tr: 'Ürününüz' },
  'productUpload.success.descriptionSuffix': { en: 'has been successfully uploaded to the marketplace.', de: 'wurde erfolgreich in den Marktplatz hochgeladen.', tr: 'başarıyla pazara yüklendi.' },

  // Validation error messages
  'productUpload.errors.selectProductType': { en: 'Please select a product type (Direct Buy or Auction)', de: 'Bitte wähle einen Produkttyp (Direktkauf oder Auktion)', tr: 'Lütfen bir ürün türü seçin (Doğrudan Satış veya Açık Artırma)' },
  'productUpload.errors.nameMin': { en: 'Please enter a product name (at least 3 characters)', de: 'Bitte gib einen Produktnamen ein (mindestens 3 Zeichen)', tr: 'Lütfen bir ürün adı girin (en az 3 karakter)' },
  'productUpload.errors.descriptionMin': { en: 'Please enter a description (at least 10 characters)', de: 'Bitte gib eine Beschreibung ein (mindestens 10 Zeichen)', tr: 'Lütfen bir açıklama girin (en az 10 karakter)' },
  'productUpload.errors.selectCategory': { en: 'Please select a category', de: 'Bitte wähle eine Kategorie', tr: 'Lütfen bir kategori seçin' },
  'productUpload.errors.selectSubcategory': { en: 'Please select a subcategory', de: 'Bitte wähle eine Unterkategorie', tr: 'Lütfen bir alt kategori seçin' },
  'productUpload.errors.enterBrand': { en: 'Please enter a brand', de: 'Bitte gib eine Marke ein', tr: 'Lütfen bir marka girin' },
  'productUpload.errors.auctionMinPrice': { en: 'Please enter a valid minimum bid price for the auction', de: 'Bitte gib einen gültigen Mindestgebotspreis für die Auktion ein', tr: 'Lütfen açık artırma için geçerli bir minimum teklif fiyatı girin' },
  'productUpload.errors.auctionBidIncrement': { en: 'Please enter a valid bid increment amount', de: 'Bitte gib einen gültigen Gebotsschritt ein', tr: 'Lütfen geçerli bir teklif artış miktarı girin' },
  'productUpload.errors.auctionEndTime': { en: 'Please select an auction end date and time', de: 'Bitte wähle ein Auktionsende (Datum und Uhrzeit)', tr: 'Lütfen açık artırma bitiş tarih ve saatini seçin' },
  'productUpload.errors.auctionEndFuture': { en: 'Auction end time must be in the future', de: 'Das Auktionsende muss in der Zukunft liegen', tr: 'Açık artırma bitiş zamanı gelecekte olmalıdır' },
  'productUpload.errors.reserveHigherThanStart': { en: 'Reserve price must be higher than starting price', de: 'Der Mindestpreis muss höher als der Startpreis sein', tr: 'Rezerv fiyatı başlangıç fiyatından yüksek olmalıdır' },
  'productUpload.errors.validPrice': { en: 'Please enter a valid price', de: 'Bitte gib einen gültigen Preis ein', tr: 'Lütfen geçerli bir fiyat girin' },
  'productUpload.errors.selectMaterial': { en: 'Please select a material', de: 'Bitte wähle ein Material', tr: 'Lütfen bir malzeme seçin' },
  'productUpload.errors.stockNegative': { en: 'Stock cannot be negative', de: 'Der Lagerbestand darf nicht negativ sein', tr: 'Stok negatif olamaz' },
  'productUpload.errors.addTag': { en: 'Please add at least one tag', de: 'Bitte füge mindestens ein Tag hinzu', tr: 'Lütfen en az bir etiket ekleyin' },
  'productUpload.errors.selectColor': { en: 'Please select at least one color', de: 'Bitte wähle mindestens eine Farbe', tr: 'Lütfen en az bir renk seçin' },
  'productUpload.errors.selectSize': { en: 'Please select at least one size', de: 'Bitte wähle mindestens eine Größe', tr: 'Lütfen en az bir beden seçin' },
  'productUpload.errors.addImage': { en: 'Please add at least one image', de: 'Bitte füge mindestens ein Bild hinzu', tr: 'Lütfen en az bir görsel ekleyin' },
  'productUpload.errors.selectShipping': { en: 'Please select at least one shipping option', de: 'Bitte wähle mindestens eine Versandoption', tr: 'Lütfen en az bir kargo seçeneği seçin' },
  'productUpload.errors.addMapLocationFirst': { en: 'Please add the map location to your addresses first before proceeding!', de: 'Bitte füge den Kartenstandort zuerst zu deinen Adressen hinzu, bevor du fortfährst!', tr: 'Lütfen devam etmeden önce harita konumunu adreslerine ekle!' },
  'productUpload.errors.selectLocation': { en: 'Please select a location/address', de: 'Bitte wähle einen Standort/eine Adresse', tr: 'Lütfen bir konum/adres seçin' },
  'dashboard.products.title.user': { en: 'My Products', de: 'Meine Produkte', tr: 'Ürünlerim' },
  'dashboard.products.subtitle.user': { en: 'Manage your uploaded products and track their performance', de: 'Verwalte deine hochgeladenen Produkte und verfolge ihre Leistung', tr: 'Yüklediğin ürünleri yönet ve performanslarını takip et' },
  'dashboard.products.title.company': { en: 'Jobs', de: 'Jobs', tr: 'İş İlanları' },
  'dashboard.products.subtitle.company': { en: 'Manage your job postings and track their performance', de: 'Verwalte deine Stellenausschreibungen und verfolge ihre Leistung', tr: 'İş ilanlarını yönet ve performanslarını takip et' },
  'dashboard.products.switch.customerPrice': { en: 'Customer Price', de: 'Kundenpreis', tr: 'Müşteri Fiyatı' },
  'dashboard.products.switch.customerPrice.caption': { en: '(includes platform fees)', de: '(inkl. Plattformgebühren)', tr: '(platform ücretleri dahil)' },
  'dashboard.products.switch.basePrice': { en: 'Base Price', de: 'Basispreis', tr: 'Liste Fiyatı' },
  'dashboard.products.switch.basePrice.caption': { en: '(your listed price)', de: '(dein Listenpreis)', tr: '(listelediğin fiyat)' },
  'dashboard.products.empty.title.user': { en: 'No products found', de: 'Keine Produkte gefunden', tr: 'Ürün bulunamadı' },
  'dashboard.products.empty.subtitle.user': { en: "You haven't uploaded any products yet. Start by uploading your first product!", de: 'Du hast noch keine Produkte hochgeladen. Lade dein erstes Produkt hoch!', tr: 'Henüz ürün yüklemedin. İlk ürününü yükleyerek başla!' },
  'dashboard.products.empty.button.user': { en: 'Upload Product', de: 'Produkt hochladen', tr: 'Ürün Yükle' },
  'dashboard.products.empty.title.company': { en: 'No jobs found', de: 'Keine Jobs gefunden', tr: 'İş ilanı bulunamadı' },
  'dashboard.products.empty.subtitle.company': { en: "You haven't posted any jobs yet. Start by creating your first job posting!", de: 'Du hast noch keine Jobs veröffentlicht. Erstelle deine erste Stellenausschreibung!', tr: 'Henüz iş ilanı paylaşmadın. İlk ilanını oluşturarak başla!' },
  'dashboard.products.empty.button.company': { en: 'Post Job', de: 'Job veröffentlichen', tr: 'İlan Paylaş' },
  'dashboard.products.status.sold': { en: 'Sold', de: 'Verkauft', tr: 'Satıldı' },
  'dashboard.products.status.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'dashboard.products.status.inactive': { en: 'Inactive', de: 'Inaktiv', tr: 'Pasif' },
  'dashboard.products.badge.featured': { en: 'Featured', de: 'Hervorgehoben', tr: 'Öne Çıkan' },
  'dashboard.products.badge.auction': { en: 'Auction', de: 'Auktion', tr: 'Açık Artırma' },
  'dashboard.products.label.remote': { en: 'REMOTE', de: 'REMOTE', tr: 'UZAKTAN' },
  'dashboard.products.label.applyBy': { en: 'Apply by:', de: 'Bewerben bis:', tr: 'Son başvuru:' },
  'dashboard.products.label.start': { en: 'Start:', de: 'Start:', tr: 'Başlangıç:' },
  'dashboard.products.label.contact': { en: 'Contact:', de: 'Kontakt:', tr: 'İletişim:' },
  'dashboard.products.label.noBrand': { en: 'No Brand', de: 'Keine Marke', tr: 'Marka Yok' },
  'dashboard.products.stats.sold': { en: 'Sold', de: 'Verkauft', tr: 'Satış' },
  'dashboard.products.stats.views': { en: 'Views', de: 'Aufrufe', tr: 'Görüntüleme' },
  'dashboard.products.stats.stock': { en: 'Stock', de: 'Lager', tr: 'Stok' },
  'dashboard.products.stats.rating': { en: 'Rating', de: 'Bewertung', tr: 'Puan' },
  'dashboard.products.cache.label': { en: 'Cache: {used}/{max} pages', de: 'Cache: {used}/{max} Seiten', tr: 'Önbellek: {used}/{max} sayfa' },
  'dashboard.products.cache.clear': { en: 'Clear Cache', de: 'Cache leeren', tr: 'Önbelleği Temizle' },
  'dashboard.products.label.currentBid': { en: 'Current bid:', de: 'Aktuelles Gebot:', tr: 'Güncel teklif:' },
  'dashboard.products.button.viewProduct': { en: 'View Product', de: 'Produkt ansehen', tr: 'Ürünü Görüntüle' },
  'dashboard.products.button.view': { en: 'View', de: 'Ansehen', tr: 'Görüntüle' },
  'dashboard.products.button.edit': { en: 'Edit', de: 'Bearbeiten', tr: 'Düzenle' },
  'dashboard.products.label.lastUpdated': { en: 'Last updated:', de: 'Zuletzt aktualisiert:', tr: 'Son güncelleme:' },
  'dashboard.products.label.applications': { en: 'Applications', de: 'Bewerbungen', tr: 'Başvurular' },
  // Favorites Tab
  'dashboard.favorites.title.products': { en: 'Favorite Products', de: 'Lieblingsprodukte', tr: 'Favori Ürünler' },
  'dashboard.favorites.title.jobs': { en: 'Favorite Jobs', de: 'Lieblingsjobs', tr: 'Favori İşler' },
  'dashboard.favorites.subtitle.products': { en: "Products you've bookmarked", de: 'Produkte, die du gespeichert hast', tr: 'Kaydettiğin ürünler' },
  'dashboard.favorites.subtitle.jobs': { en: "Jobs you've bookmarked", de: 'Jobs, die du gespeichert hast', tr: 'Kaydettiğin işler' },
  'dashboard.favorites.empty.title.products': { en: 'No favorite products yet', de: 'Noch keine Lieblingsprodukte', tr: 'Henüz favori ürün yok' },
  'dashboard.favorites.empty.subtitle.products': { en: 'Start exploring products and add them to your favorites!', de: 'Entdecke Produkte und füge sie deinen Favoriten hinzu!', tr: 'Ürünleri keşfet ve favorilerine ekle!' },
  'dashboard.favorites.empty.title.jobs': { en: 'No favorite jobs yet', de: 'Noch keine Lieblingsjobs', tr: 'Henüz favori iş yok' },
  'dashboard.favorites.empty.subtitle.jobs': { en: 'Start exploring jobs and add them to your favorites!', de: 'Entdecke Jobs und füge sie deinen Favoriten hinzu!', tr: 'İşleri keşfet ve favorilerine ekle!' },
  'dashboard.favorites.button.browse.products': { en: 'Browse Products', de: 'Produkte entdecken', tr: 'Ürünlere Göz At' },
  'dashboard.favorites.button.browse.jobs': { en: 'Browse Jobs', de: 'Jobs entdecken', tr: 'İşlere Göz At' },
  'dashboard.favorites.toast.removed': { en: 'Removed from favorites', de: 'Aus Favoriten entfernt', tr: 'Favorilerden kaldırıldı' },
  'dashboard.favorites.error.load': { en: 'Failed to load favorites', de: 'Favoriten konnten nicht geladen werden', tr: 'Favoriler yüklenemedi' },
  'dashboard.favorites.badge.yourProduct': { en: 'YOUR PRODUCT', de: 'DEIN PRODUKT', tr: 'SENİN ÜRÜNÜN' },
  'dashboard.favorites.badge.yourJob': { en: 'YOUR JOB', de: 'DEIN JOB', tr: 'SENİN İŞİN' },
  'dashboard.favorites.button.viewProduct': { en: 'View Product', de: 'Produkt ansehen', tr: 'Ürünü Görüntüle' },
  'dashboard.favorites.button.viewJob': { en: 'View Job', de: 'Job ansehen', tr: 'İlanı Görüntüle' },
  // Auctions Tab
  'dashboard.auctions.title': { en: 'My Auctions', de: 'Meine Auktionen', tr: 'Açık Artırmalarım' },
  'dashboard.auctions.subtitle': { en: 'Manage your product auctions and track bidding activity', de: 'Verwalte deine Produktauktionen und verfolge Gebotsaktivitäten', tr: 'Ürün açık artırmalarını yönet ve teklif faaliyetlerini takip et' },
  'dashboard.auctions.create': { en: 'Create Auction', de: 'Auktion erstellen', tr: 'Açık Artırma Oluştur' },
  'dashboard.auctions.filter.label': { en: 'Filter by status:', de: 'Nach Status filtern:', tr: 'Duruma göre filtrele:' },
  'dashboard.auctions.filter.all': { en: 'All Auctions', de: 'Alle Auktionen', tr: 'Tüm Açık Artırmalar' },
  'dashboard.auctions.filter.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'dashboard.auctions.filter.ended': { en: 'Ended', de: 'Beendet', tr: 'Bitti' },
  'dashboard.auctions.filter.cancelled': { en: 'Cancelled', de: 'Abgebrochen', tr: 'İptal Edildi' },
  'dashboard.auctions.empty.title.all': { en: 'No auctions yet', de: 'Noch keine Auktionen', tr: 'Henüz açık artırma yok' },
  'dashboard.auctions.empty.title.active': { en: 'No active auctions', de: 'Keine aktiven Auktionen', tr: 'Aktif açık artırma yok' },
  'dashboard.auctions.empty.title.ended': { en: 'No ended auctions', de: 'Keine beendeten Auktionen', tr: 'Biten açık artırma yok' },
  'dashboard.auctions.empty.title.cancelled': { en: 'No cancelled auctions', de: 'Keine abgebrochenen Auktionen', tr: 'İptal edilen açık artırma yok' },
  'dashboard.auctions.empty.subtitle.all': { en: 'Create your first auction to start selling products through bidding. Set starting prices, reserve prices, and let buyers compete for your items!', de: 'Erstelle deine erste Auktion, um Produkte per Gebot zu verkaufen. Lege Start- und Mindestpreise fest und lass Käufer bieten!', tr: 'Teklif yoluyla satışa başlamak için ilk açık artırmanı oluştur. Başlangıç ve rezerv fiyatlarını ayarla, alıcılar yarışsın!' },
  'dashboard.auctions.empty.subtitle.active': { en: "You don't have any active auctions at the moment. Create a new auction to get started!", de: 'Du hast derzeit keine aktiven Auktionen. Erstelle eine neue Auktion!', tr: 'Şu anda aktif açık artırman yok. Yeni bir açık artırma oluştur!' },
  'dashboard.auctions.empty.subtitle.ended': { en: "You don't have any ended auctions yet. Your completed auctions will appear here.", de: 'Du hast noch keine beendeten Auktionen. Abgeschlossene Auktionen erscheinen hier.', tr: 'Henüz biten açık artırman yok. Tamamlanan açık artırmalar burada görünecek.' },
  'dashboard.auctions.empty.subtitle.cancelled': { en: "You don't have any cancelled auctions.", de: 'Du hast keine abgebrochenen Auktionen.', tr: 'İptal edilen açık artırman yok.' },
  'dashboard.auctions.overlay.ended': { en: 'Auction Ended', de: 'Auktion beendet', tr: 'Açık artırma bitti' },
  'dashboard.auctions.status.active': { en: '🟢 Active', de: '🟢 Aktiv', tr: '🟢 Aktif' },
  'dashboard.auctions.status.cancelled': { en: '❌ Cancelled', de: '❌ Abgebrochen', tr: '❌ İptal Edildi' },
  'dashboard.auctions.status.paused': { en: '⏸️ Paused', de: '⏸️ Pausiert', tr: '⏸️ Duraklatıldı' },
  'dashboard.auctions.status.draft': { en: '📝 Draft', de: '📝 Entwurf', tr: '📝 Taslak' },
  'dashboard.auctions.stats.startingPrice': { en: 'Starting Price', de: 'Startpreis', tr: 'Başlangıç Fiyatı' },
  'dashboard.auctions.stats.currentBid': { en: 'Current Bid', de: 'Aktuelles Gebot', tr: 'Güncel Teklif' },
  'dashboard.auctions.stats.finalBid': { en: 'Final Bid', de: 'Endgebot', tr: 'Son Teklif' },
  'dashboard.auctions.stats.reservePrice': { en: 'Reserve Price', de: 'Mindestpreis', tr: 'Rezerv Fiyatı' },
  'dashboard.auctions.stats.totalBids': { en: 'Total Bids', de: 'Gebote gesamt', tr: 'Toplam Teklif' },
  'dashboard.auctions.time.started': { en: 'Started', de: 'Gestartet', tr: 'Başladı' },
  'dashboard.auctions.time.ends': { en: 'Ends', de: 'Endet', tr: 'Biter' },
  'dashboard.auctions.time.ended': { en: 'Ended', de: 'Beendet', tr: 'Bitti' },
  'dashboard.auctions.time.duration': { en: 'Duration', de: 'Dauer', tr: 'Süre' },
  'dashboard.auctions.noImage': { en: 'No Image', de: 'Kein Bild', tr: 'Görsel Yok' },
  'dashboard.auctions.button.viewResults': { en: 'View Results', de: 'Ergebnisse ansehen', tr: 'Sonuçları Gör' },
  'dashboard.auctions.button.viewProduct': { en: 'View Product', de: 'Produkt ansehen', tr: 'Ürünü Görüntüle' },
  'dashboard.auctions.button.viewAuction': { en: 'View Auction', de: 'Auktion ansehen', tr: 'Açık Artırmayı Gör' },
  // Offers Tab
  'dashboard.offers.title.user': { en: 'Offers', de: 'Angebote', tr: 'Teklifler' },
  'dashboard.offers.title.company': { en: 'Applications Received', de: 'Erhaltene Bewerbungen', tr: 'Alınan Başvurular' },
  'dashboard.offers.title.jobUser': { en: 'Applications', de: 'Bewerbungen', tr: 'Başvurular' },
  'dashboard.offers.subtitle.user': { en: 'View offers you made and offers you received for your products', de: 'Sieh dir deine abgegebenen und erhaltenen Angebote für deine Produkte an', tr: 'Ürünlerin için yaptığın ve aldığın teklifleri görüntüle' },
  'dashboard.offers.subtitle.company': { en: 'View applications received for your postings', de: 'Sieh dir Bewerbungen für deine Ausschreibungen an', tr: 'İlanların için alınan başvuruları görüntüle' },
  'dashboard.offers.subtitle.jobUser': { en: 'View applications you submitted for jobs', de: 'Sieh dir deine gesendeten Bewerbungen an', tr: 'İşler için gönderdiğin başvuruları görüntüle' },
  'dashboard.offers.buttons.made': { en: 'Offers I Made', de: 'Meine Angebote', tr: 'Yaptığım Teklifler' },
  'dashboard.offers.buttons.received': { en: 'Offers I Received', de: 'Erhaltene Angebote', tr: 'Aldığım Teklifler' },
  'dashboard.offers.loading': { en: 'Loading offers...', de: 'Angebote werden geladen...', tr: 'Teklifler yükleniyor...' },
  'dashboard.offers.error.load': { en: 'Failed to load offers', de: 'Angebote konnten nicht geladen werden', tr: 'Teklifler yüklenemedi' },
  'dashboard.offers.empty.made.title': { en: 'No offers made yet', de: 'Noch keine Angebote abgegeben', tr: 'Henüz teklif vermedin' },
  'dashboard.offers.empty.made.subtitle': { en: 'Start browsing products and make your first offer!', de: 'Entdecke Produkte und gib dein erstes Angebot ab!', tr: 'Ürünlere göz at ve ilk teklifini ver!' },
  'dashboard.offers.empty.received.title': { en: 'No offers received yet', de: 'Noch keine Angebote erhalten', tr: 'Henüz teklif almadın' },
  'dashboard.offers.empty.received.subtitle': { en: 'Your received offers will appear here.', de: 'Deine erhaltenen Angebote erscheinen hier.', tr: 'Aldığın teklifler burada görünecek.' },
  'dashboard.offers.filters.title': { en: 'Filters', de: 'Filter', tr: 'Filtreler' },
  'dashboard.offers.filters.allStatus': { en: 'All Status', de: 'Alle Status', tr: 'Tüm Durumlar' },
  'dashboard.offers.filters.pending': { en: 'Pending', de: 'Ausstehend', tr: 'Beklemede' },
  'dashboard.offers.filters.accepted': { en: 'Accepted', de: 'Angenommen', tr: 'Kabul edildi' },
  'dashboard.offers.filters.declined': { en: 'Declined', de: 'Abgelehnt', tr: 'Reddedildi' },
  'dashboard.offers.filters.countered': { en: 'Countered', de: 'Gegengebot', tr: 'Karşı teklif' },
  'dashboard.offers.filters.expired': { en: 'Expired', de: 'Abgelaufen', tr: 'Süresi doldu' },
  'dashboard.offers.filters.minPrice': { en: 'Min $', de: 'Min $', tr: 'Min $' },
  'dashboard.offers.filters.maxPrice': { en: 'Max $', de: 'Max $', tr: 'Maks $' },
  'dashboard.offers.filters.clear': { en: 'Clear', de: 'Leeren', tr: 'Temizle' },
  'dashboard.offers.status.pending': { en: 'Pending', de: 'Ausstehend', tr: 'Beklemede' },
  'dashboard.offers.status.accepted': { en: 'Accepted', de: 'Angenommen', tr: 'Kabul edildi' },
  'dashboard.offers.status.declined': { en: 'Declined', de: 'Abgelehnt', tr: 'Reddedildi' },
  'dashboard.offers.status.countered': { en: 'Countered', de: 'Gegengebot', tr: 'Karşı teklif' },
  'dashboard.offers.status.expired': { en: 'Expired', de: 'Abgelaufen', tr: 'Süresi doldu' },
  'dashboard.offers.status.completed': { en: 'Completed', de: 'Abgeschlossen', tr: 'Tamamlandı' },
  'dashboard.offers.button.openChat': { en: 'Open Chat', de: 'Chat öffnen', tr: 'Sohbeti Aç' },
  // Orders Tab
  'dashboard.orders.empty.sold.title': { en: 'No Products Sold Yet', de: 'Noch keine Produkte verkauft', tr: 'Henüz satılan ürün yok' },
  'dashboard.orders.empty.sold.subtitle': { en: 'Start selling your products to see them here', de: 'Beginne mit dem Verkauf deiner Produkte, um sie hier zu sehen', tr: 'Ürünlerini satmaya başla, burada görünecek' },
  'dashboard.orders.empty.sold.cta': { en: 'Upload Your First Product', de: 'Dein erstes Produkt hochladen', tr: 'İlk ürününü yükle' },
  'dashboard.orders.empty.bought.title': { en: 'No Products Bought Yet', de: 'Noch keine Produkte gekauft', tr: 'Henüz satın alınan ürün yok' },
  'dashboard.orders.empty.bought.subtitle': { en: 'Browse and buy products to see your purchases here', de: 'Durchstöbere und kaufe Produkte, um deine Einkäufe hier zu sehen', tr: 'Ürünlere göz at ve satın al, alışverişlerin burada görünecek' },
  'dashboard.orders.empty.bought.cta': { en: 'Browse Products', de: 'Produkte durchstöbern', tr: 'Ürünlere Göz At' },
  'dashboard.orders.status.succeeded': { en: 'Transaction Complete', de: 'Transaktion abgeschlossen', tr: 'İşlem tamamlandı' },
  'dashboard.orders.status.pending': { en: 'Pending', de: 'Ausstehend', tr: 'Beklemede' },
  'dashboard.orders.status.requires_action': { en: 'Action Required', de: 'Aktion erforderlich', tr: 'İşlem gerekli' },
  'dashboard.orders.status.refunded': { en: 'Refunded', de: 'Erstattet', tr: 'İade edildi' },
  'dashboard.orders.status.failed': { en: 'Payment Failed', de: 'Zahlung fehlgeschlagen', tr: 'Ödeme başarısız' },
  'dashboard.orders.status.cancelled': { en: 'Cancelled', de: 'Storniert', tr: 'İptal edildi' },
  'dashboard.orders.fallback.noDescription': { en: 'No description', de: 'Keine Beschreibung', tr: 'Açıklama yok' },
  'dashboard.orders.toast.paymentCompleted': { en: 'Payment completed successfully!', de: 'Zahlung erfolgreich abgeschlossen!', tr: 'Ödeme başarıyla tamamlandı!' },
  'dashboard.orders.toast.refreshFailed': { en: 'Failed to refresh orders', de: 'Bestellungen konnten nicht aktualisiert werden', tr: 'Siparişler yenilenemedi' },
  'dashboard.orders.button.payNow': { en: 'Pay Now', de: 'Jetzt bezahlen', tr: 'Şimdi Öde' },
  'dashboard.orders.button.leaveReview': { en: 'Leave Review', de: 'Bewertung abgeben', tr: 'Değerlendirme yap' },
  'dashboard.orders.review.availableAfter': { en: 'Available after 24 hours of purchase — ~{hours}h remaining', de: 'Verfügbar 24 Stunden nach dem Kauf — ~{hours} Std. verbleibend', tr: 'Satın almadan 24 saat sonra kullanılabilir — ~{hours}s kaldı' },
  'dashboard.orders.title': { en: 'Orders', de: 'Bestellungen', tr: 'Siparişler' },
  'dashboard.orders.subtitle': { en: 'Track your recent orders and their status', de: 'Verfolge deine aktuellen Bestellungen und deren Status', tr: 'Son siparişlerini ve durumlarını takip et' },
  'dashboard.orders.toggle.sold': { en: 'Sold Products', de: 'Verkaufte Produkte', tr: 'Satılan Ürünler' },
  'dashboard.orders.toggle.bought': { en: 'Bought Products', de: 'Gekaufte Produkte', tr: 'Satın Alınan Ürünler' },
  'dashboard.orders.section.sold': { en: 'Sold Products', de: 'Verkaufte Produkte', tr: 'Satılan Ürünler' },
  'dashboard.orders.section.bought': { en: 'Bought Products', de: 'Gekaufte Produkte', tr: 'Satın Alınan Ürünler' },
  'dashboard.orders.tracking.label': { en: 'Tracking:', de: 'Sendungsverfolgung:', tr: 'Takip:' },
  'dashboard.orders.timeline.offerMade': { en: 'Offer Made', de: 'Angebot gemacht', tr: 'Teklif Verildi' },
  'dashboard.orders.timeline.orderCreated': { en: 'Order Created', de: 'Bestellung erstellt', tr: 'Sipariş Oluşturuldu' },
  'dashboard.orders.timeline.payment': { en: 'Payment', de: 'Zahlung', tr: 'Ödeme' },
  'dashboard.orders.timeline.preparing': { en: 'Preparing', de: 'Vorbereitung', tr: 'Hazırlanıyor' },
  'dashboard.orders.timeline.shipped': { en: 'Shipped', de: 'Versandt', tr: 'Kargolandı' },
  'dashboard.orders.timeline.inTransit': { en: 'In Transit', de: 'Unterwegs', tr: 'Yolda' },
  'dashboard.orders.timeline.outForDelivery': { en: 'Out for Delivery', de: 'In Zustellung', tr: 'Dağıtıma Çıktı' },
  'dashboard.orders.timeline.delivered': { en: 'Delivered', de: 'Zugestellt', tr: 'Teslim Edildi' },
  'dashboard.orders.timeline.deliveryFailed': { en: 'Delivery Failed', de: 'Zustellung fehlgeschlagen', tr: 'Teslimat Başarısız' },
  'dashboard.orders.timeline.returned': { en: 'Returned', de: 'Zurückgesendet', tr: 'İade Edildi' },
  'dashboard.orders.timeline.deliveryPending': { en: 'Delivery Pending', de: 'Zustellung ausstehend', tr: 'Teslimat Bekleniyor' },
  // Pagination (shared)
  'pagination.showing': { en: 'Showing', de: 'Anzeige', tr: 'Gösteriliyor' },
  'pagination.to': { en: 'to', de: 'bis', tr: 'ile' },
  'pagination.of': { en: 'of', de: 'von', tr: '/' },
  'pagination.products': { en: 'products', de: 'Produkte', tr: 'ürün' },
  'pagination.perPage': { en: 'Per Page', de: 'Pro Seite', tr: 'Sayfa Başına' },
  'pagination.items': { en: 'items', de: 'Artikel', tr: 'öğe' },
  'pagination.pageOf': { en: 'Page {page} of {total}', de: 'Seite {page} von {total}', tr: '{total} sayfadan {page}. sayfa' },
  // Settings Modal
  'settings.title.user': { en: 'Settings', de: 'Einstellungen', tr: 'Ayarlar' },
  // User Profile Page
  'userProfile.cover.edit': { en: 'Click to edit cover image', de: 'Zum Bearbeiten des Titelbilds klicken', tr: 'Kapak görselini düzenlemek için tıklayın' },
  'userProfile.reviews': { en: 'reviews', de: 'Bewertungen', tr: 'değerlendirme' },
  'userProfile.location.unknown': { en: 'Unknown location', de: 'Unbekannter Ort', tr: 'Bilinmeyen konum' },
  'userProfile.memberSince': { en: 'Member since {year}', de: 'Mitglied seit {year}', tr: '{year} yılından beri üye' },
  'userProfile.buttons.editProfile': { en: 'Edit Profile', de: 'Profil bearbeiten', tr: 'Profili Düzenle' },
  'userProfile.buttons.sendMessage': { en: 'Send Message', de: 'Nachricht senden', tr: 'Mesaj Gönder' },
  'userProfile.stats.title': { en: 'Profile Statistics', de: 'Profilstatistiken', tr: 'Profil İstatistikleri' },
  'userProfile.stats.productsListed': { en: 'Products Listed', de: 'Eingestellte Produkte', tr: 'Listelenen Ürünler' },
  'userProfile.stats.totalSold': { en: 'Total Sold', de: 'Insgesamt verkauft', tr: 'Toplam Satılan' },
  'userProfile.stats.totalViews': { en: 'Total Views', de: 'Gesamtaufrufe', tr: 'Toplam Görüntüleme' },
  'userProfile.stats.averageRating': { en: 'Average Rating', de: 'Durchschnittliche Bewertung', tr: 'Ortalama Puan' },
  'userProfile.about.title': { en: 'About {name}', de: 'Über {name}', tr: '{name} Hakkında' },
  'userProfile.about.noDescription': { en: 'No description provided.', de: 'Keine Beschreibung vorhanden.', tr: 'Açıklama yok.' },
  'userProfile.location.notSpecified': { en: 'Location not specified', de: 'Ort nicht angegeben', tr: 'Konum belirtilmedi' },
  'userProfile.joinedOn': { en: 'Joined {date}', de: 'Beigetreten am {date}', tr: '{date} tarihinde katıldı' },
  'userProfile.email.notProvided': { en: 'Email not provided', de: 'E-Mail nicht angegeben', tr: 'E-posta belirtilmedi' },
  'userProfile.interests.title': { en: 'Interests & Categories', de: 'Interessen & Kategorien', tr: 'İlgi Alanları ve Kategoriler' },
  'userProfile.documents.title': { en: 'Public Documents', de: 'Öffentliche Dokumente', tr: 'Herkese Açık Belgeler' },
  'userProfile.documents.empty.title': { en: 'No public documents', de: 'Keine öffentlichen Dokumente', tr: 'Herkese açık belge yok' },
  'userProfile.documents.empty.subtitleSelf': { en: "You haven't made any documents public yet. Make documents public in your document manager to share them here.", de: 'Du hast noch keine Dokumente öffentlich gemacht. Mache Dokumente im Dokumentenmanager öffentlich, um sie hier zu teilen.', tr: 'Henüz hiçbir belgeyi herkese açık yapmadın. Burada paylaşmak için belge yöneticisinde belgeleri herkese açık yap.' },
  'userProfile.documents.empty.subtitleOther': { en: "This user hasn't shared any public documents yet.", de: 'Dieser Benutzer hat noch keine öffentlichen Dokumente geteilt.', tr: 'Bu kullanıcı henüz herkese açık belge paylaşmadı.' },
  'userProfile.buttons.view': { en: 'View', de: 'Ansehen', tr: 'Görüntüle' },
  'userProfile.buttons.download': { en: 'Download', de: 'Herunterladen', tr: 'İndir' },
  'userProfile.loading.checkingAccess': { en: 'Checking profile access...', de: 'Profilzugriff wird geprüft...', tr: 'Profil erişimi kontrol ediliyor...' },
  'userProfile.breadcrumb.home': { en: 'Home', de: 'Startseite', tr: 'Ana Sayfa' },
  'userProfile.breadcrumb.profile': { en: 'Profile', de: 'Profil', tr: 'Profil' },
  'userProfile.products.title.mine': { en: 'My Products', de: 'Meine Produkte', tr: 'Ürünlerim' },
  'userProfile.products.title.user': { en: "{name}'s Products", de: 'Produkte von {name}', tr: '{name} Ürünleri' },
  'userProfile.products.manageListed': { en: 'Manage your {count} listed products', de: 'Verwalte deine {count} eingestellten Produkte', tr: '{count} listelenen ürününü yönet' },
  'userProfile.products.userListedCount': { en: '{name} has {count} listed products', de: '{name} hat {count} eingestellte Produkte', tr: '{name} {count} listelenen ürüne sahip' },
  'userProfile.buttons.addNewProduct': { en: 'Add New Product', de: 'Neues Produkt hinzufügen', tr: 'Yeni Ürün Ekle' },
  'userProfile.products.empty.title': { en: 'No products found', de: 'Keine Produkte gefunden', tr: 'Ürün bulunamadı' },
  'userProfile.products.empty.subtitleSelf': { en: "You haven't uploaded any products yet. Start by uploading your first product!", de: 'Du hast noch keine Produkte hochgeladen. Lade dein erstes Produkt hoch!', tr: 'Henüz ürün yüklemedin. İlk ürününü yükleyerek başla!' },
  'userProfile.products.empty.subtitleOther': { en: "This user hasn't uploaded any products yet.", de: 'Dieser Benutzer hat noch keine Produkte hochgeladen.', tr: 'Bu kullanıcı henüz ürün yüklemedi.' },
  'userProfile.buttons.uploadProduct': { en: 'Upload Product', de: 'Produkt hochladen', tr: 'Ürün Yükle' },
  'userProfile.buttons.viewAllMyProducts': { en: 'View All My Products', de: 'Alle meine Produkte ansehen', tr: 'Tüm Ürünlerimi Görüntüle' },
  'userProfile.tabs.reviews': { en: 'Reviews', de: 'Bewertungen', tr: 'Değerlendirmeler' },
  'userProfile.tabs.about': { en: 'About', de: 'Über', tr: 'Hakkında' },
  'userProfile.contact.title': { en: 'Contact Information', de: 'Kontaktinformationen', tr: 'İletişim Bilgileri' },
  'userProfile.stock.inStock': { en: '{count} in stock', de: '{count} auf Lager', tr: '{count} stokta' },
  'userProfile.sold.count': { en: '{count} sold', de: '{count} verkauft', tr: '{count} satıldı' },
  'userProfile.reviews.title': { en: 'Customer Reviews', de: 'Kundenbewertungen', tr: 'Müşteri Değerlendirmeleri' },
  'userProfile.reviews.productPrefix': { en: 'Product:', de: 'Produkt:', tr: 'Ürün:' },
  'userProfile.reviews.helpful': { en: '{count} helpful', de: '{count} hilfreich', tr: '{count} faydalı' },
  'settings.title.platform': { en: 'Platform Settings', de: 'Plattform-Einstellungen', tr: 'Platform Ayarları' },
  'settings.tabs.profile': { en: 'Profile', de: 'Profil', tr: 'Profil' },
  'settings.tabs.password': { en: 'Password', de: 'Passwort', tr: 'Şifre' },
  'settings.tabs.notifications': { en: 'Notifications', de: 'Benachrichtigungen', tr: 'Bildirimler' },
  'settings.tabs.paymentMethods': { en: 'Payment Methods', de: 'Zahlungsmethoden', tr: 'Ödeme Yöntemleri' },
  'settings.tabs.platformPaymentMethods': { en: 'Platform Payment Methods', de: 'Plattform-Zahlungsmethoden', tr: 'Platform Ödeme Yöntemleri' },
  'settings.tabs.platformFees': { en: 'Platform Fees', de: 'Plattformgebühren', tr: 'Platform Ücretleri' },
  'settings.tabs.addresses': { en: 'Addresses', de: 'Adressen', tr: 'Adresler' },
  'settings.tabs.apiKeys': { en: 'API Keys', de: 'API-Schlüssel', tr: 'API Anahtarları' },
  'settings.profile.title': { en: 'Profile', de: 'Profil', tr: 'Profil' },
  'settings.profile.subtitle': { en: 'Control how your profile appears and what information is visible.', de: 'Passe an, wie dein Profil angezeigt wird und welche Infos sichtbar sind.', tr: 'Profilinizin nasıl görüneceğini ve hangi bilgilerin görünür olacağını ayarlayın.' },
  'settings.profile.section.visibility': { en: 'Visibility', de: 'Sichtbarkeit', tr: 'Görünürlük' },
  'settings.profile.visibility.public': { en: 'Public profile', de: 'Öffentliches Profil', tr: 'Herkese açık profil' },
  'settings.profile.visibility.private': { en: 'Private profile', de: 'Privates Profil', tr: 'Gizli profil' },
  'settings.profile.visibility.publicHelp': { en: 'Anyone can view your profile.', de: 'Jeder kann dein Profil sehen.', tr: 'Herkes profilinizi görebilir.' },
  'settings.profile.visibility.privateHelp': { en: 'Only you (and possibly approved users) can view your profile.', de: 'Nur du (und ggf. freigegebene Nutzer) können dein Profil sehen.', tr: 'Yalnızca siz (ve gerekirse onaylı kullanıcılar) profilinizi görebilir.' },
  'settings.profile.section.info': { en: 'Profile info', de: 'Profilinfos', tr: 'Profil bilgileri' },
  'settings.profile.field.location': { en: 'Location', de: 'Ort', tr: 'Konum' },
  'settings.profile.field.website': { en: 'Website', de: 'Website', tr: 'Web sitesi' },
  'settings.profile.field.bio': { en: 'Bio', de: 'Bio', tr: 'Hakkında' },
  'settings.profile.section.preferences': { en: 'Preferences', de: 'Präferenzen', tr: 'Tercihler' },
  'settings.profile.notifications': { en: 'Enable notifications', de: 'Benachrichtigungen aktivieren', tr: 'Bildirimleri etkinleştir' },
  // Password Tab
  'settings.password.title': { en: 'Change Password', de: 'Passwort ändern', tr: 'Şifreyi Değiştir' },
  // Product Detail Page
  'productDetail.breadcrumb.home': { en: 'Home', de: 'Startseite', tr: 'Ana Sayfa' },
  'productDetail.breadcrumb.products': { en: 'Products', de: 'Produkte', tr: 'Ürünler' },
  'productDetail.breadcrumb.jobs': { en: 'Jobs', de: 'Jobs', tr: 'İşler' },
  'productDetail.status.yourProduct': { en: 'Your Product', de: 'Dein Produkt', tr: 'Ürünün' },
  'productDetail.status.yourJob': { en: 'Your Job', de: 'Dein Job', tr: 'İşin' },
  'productDetail.status.inactive': { en: 'Inactive', de: 'Inaktiv', tr: 'Pasif' },
  'productDetail.status.sold': { en: 'Sold', de: 'Verkauft', tr: 'Satıldı' },
  'productDetail.status.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'productDetail.status.auction': { en: 'Auction', de: 'Auktion', tr: 'Açık Artırma' },
  'productDetail.status.privateSale': { en: 'Private Sale', de: 'Privatverkauf', tr: 'Özel Satış' },
  'productDetail.status.privateSaleMakeOffer': { en: 'Private Sale - Make Offer', de: 'Privatverkauf - Angebot machen', tr: 'Özel Satış - Teklif Ver' },
  'productDetail.rating.reviews': { en: 'reviews', de: 'Bewertungen', tr: 'değerlendirme' },
  'productDetail.auction.currentBid': { en: 'Current Highest Bid', de: 'Aktuelles Höchstgebot', tr: 'Güncel En Yüksek Teklif' },
  'productDetail.auction.ended': { en: 'Auction Ended', de: 'Auktion beendet', tr: 'Açık artırma bitti' },
  'productDetail.loading': { en: 'Loading...', de: 'Wird geladen...', tr: 'Yükleniyor...' },
  'productDetail.description.fallback': { en: 'No description available.', de: 'Keine Beschreibung verfügbar.', tr: 'Açıklama mevcut değil.' },
  'productDetail.features': { en: 'Features', de: 'Merkmale', tr: 'Özellikler' },
  'productDetail.requiredSkills': { en: 'Required Skills', de: 'Erforderliche Fähigkeiten', tr: 'Gerekli Beceriler' },
  'productDetail.job.details': { en: 'Job Details', de: 'Jobdetails', tr: 'İş Detayları' },
  'productDetail.job.positions': { en: '{count} Position Available', de: '{count} Stelle verfügbar', tr: '{count} Pozisyon Mevcut' },
  'productDetail.job.fullTime': { en: 'Full-time', de: 'Vollzeit', tr: 'Tam zamanlı' },
  'productDetail.job.remoteOk': { en: 'Remote OK', de: 'Remote möglich', tr: 'Uzaktan uygun' },
  'productDetail.quantity.title': { en: 'Quantity', de: 'Menge', tr: 'Adet' },
  'productDetail.stock.inStock': { en: '{count} in stock', de: '{count} auf Lager', tr: '{count} stokta' },
  'productDetail.stock.outOfStock': { en: 'Out of stock', de: 'Nicht auf Lager', tr: 'Stokta yok' },
  'productDetail.cta.makeOffer': { en: 'Make Offer', de: 'Angebot machen', tr: 'Teklif Ver' },
  'productDetail.cta.addToCart': { en: 'Add to Cart', de: 'In den Warenkorb', tr: 'Sepete Ekle' },
  'productDetail.cta.outOfStock': { en: 'Out of Stock', de: 'Nicht auf Lager', tr: 'Stokta Yok' },
  'productDetail.cta.signInTo': { en: 'Sign in to {action}', de: 'Zum {action} anmelden', tr: '{action} için giriş yapın' },
  'productDetail.cta.apply': { en: 'Apply', de: 'Bewerben', tr: 'Başvur' },
  'productDetail.cta.buy': { en: 'Buy', de: 'Kaufen', tr: 'Satın Al' },
  'productDetail.cta.contactSeller': { en: 'Contact Seller', de: 'Verkäufer kontaktieren', tr: 'Satıcıyla İletişime Geç' },
  'productDetail.sendVendorMessage': { en: 'Send Vendor Message', de: 'Nachricht an Verkäufer senden', tr: 'Satıcıya Mesaj Gönder' },
  'productDetail.signInToContact': { en: 'Sign in to Contact Seller', de: 'Zum Kontaktieren anmelden', tr: 'Satıcıyla iletişime geçmek için giriş yapın' },
  'productDetail.liveAuction': { en: 'Live Auction', de: 'Live-Auktion', tr: 'Canlı Açık Artırma' },
  'productDetail.auction.reservePrice': { en: 'Reserve Price:', de: 'Mindestpreis:', tr: 'Asgari Fiyat:' },
  'productDetail.auction.met': { en: 'Met', de: 'Erreicht', tr: 'Karşılandı' },
  'productDetail.auction.needMore': { en: 'Need ${amount} more', de: 'Noch ${amount} nötig', tr: '${amount} daha gerekli' },
  'productDetail.viewBids': { en: 'View Bids', de: 'Gebote anzeigen', tr: 'Teklifleri Gör' },
  'productDetail.shipping.title': { en: 'Shipping Options', de: 'Versandoptionen', tr: 'Kargo Seçenekleri' },
  'productDetail.shipping.none': { en: 'No shipping options configured for this product.', de: 'Für dieses Produkt sind keine Versandoptionen konfiguriert.', tr: 'Bu ürün için kargo seçeneği yapılandırılmadı.' },
  // Seller Information
  'productDetail.seller.title': { en: 'Seller Information', de: 'Verkäuferinformationen', tr: 'Satıcı Bilgileri' },
  'productDetail.seller.verified': { en: 'Verified Seller', de: 'Verifizierter Verkäufer', tr: 'Doğrulanmış Satıcı' },
  'productDetail.seller.role.seller': { en: 'Seller', de: 'Verkäufer', tr: 'Satıcı' },
  'productDetail.seller.trustedBlurb': { en: 'Trusted seller with excellent customer service and fast shipping.', de: 'Vertrauenswürdiger Verkäufer mit ausgezeichnetem Kundenservice und schnellem Versand.', tr: 'Hızlı kargo ve mükemmel müşteri hizmetleriyle güvenilir satıcı.' },
  'productDetail.seller.memberSince': { en: 'Member since {year}', de: 'Mitglied seit {year}', tr: '{year} yılından beri üye' },
  'productDetail.seller.viewStore': { en: 'View Store', de: 'Shop ansehen', tr: 'Mağazayı Gör' },
  'productDetail.seller.sales': { en: '{count} sales', de: '{count} Verkäufe', tr: '{count} satış' },
  // Product Specifications
  'productDetail.specs.title': { en: 'Product Specifications', de: 'Produktspezifikationen', tr: 'Ürün Özellikleri' },
  'productDetail.specs.brand': { en: 'Brand', de: 'Marke', tr: 'Marka' },
  'productDetail.specs.category': { en: 'Category', de: 'Kategorie', tr: 'Kategori' },
  'productDetail.specs.subcategory': { en: 'Subcategory', de: 'Unterkategorie', tr: 'Alt Kategori' },
  'productDetail.specs.condition': { en: 'Condition', de: 'Zustand', tr: 'Durum' },
  'productDetail.specs.material': { en: 'Material', de: 'Material', tr: 'Malzeme' },
  'productDetail.specs.weight': { en: 'Weight', de: 'Gewicht', tr: 'Ağırlık' },
  'productDetail.specs.dimensions': { en: 'Dimensions', de: 'Abmessungen', tr: 'Boyutlar' },
  'productDetail.specs.stock': { en: 'Stock', de: 'Bestand', tr: 'Stok' },
  'productDetail.specs.notSpecified': { en: 'Not specified', de: 'Nicht angegeben', tr: 'Belirtilmemiş' },
  'productDetail.specs.available': { en: '{count} available', de: '{count} verfügbar', tr: '{count} mevcut' },
  // Buttons
  'productDetail.buttons.editProduct': { en: 'Edit Product', de: 'Produkt bearbeiten', tr: 'Ürünü Düzenle' },
  'productDetail.buttons.editJob': { en: 'Edit Job', de: 'Job bearbeiten', tr: 'İşi Düzenle' },
  'productDetail.buttons.back': { en: 'Back', de: 'Zurück', tr: 'Geri' },
  // Tabs
  'productDetail.tabs.description': { en: 'Description', de: 'Beschreibung', tr: 'Açıklama' },
  
  // Edit Product Page
  'editProduct.title': { en: 'Edit Product', de: 'Produkt bearbeiten', tr: 'Ürünü Düzenle' },
  'editProduct.steps.basic': { en: 'Basic Information', de: 'Grundinformationen', tr: 'Temel Bilgiler' },
  'editProduct.steps.detailsImages': { en: 'Details & Images', de: 'Details & Bilder', tr: 'Detaylar ve Görseller' },
  'editProduct.steps.shipping': { en: 'Shipping', de: 'Versand', tr: 'Kargo' },
  'editProduct.steps.reviewPublish': { en: 'Review & Publish', de: 'Überprüfen & Veröffentlichen', tr: 'Gözden Geçir ve Yayınla' },
  'editProduct.buttons.back': { en: 'Back', de: 'Zurück', tr: 'Geri' },
  'editProduct.buttons.save': { en: 'Save', de: 'Speichern', tr: 'Kaydet' },
  'editProduct.buttons.next': { en: 'Next', de: 'Weiter', tr: 'İleri' },
  'editProduct.buttons.preview': { en: 'Preview', de: 'Vorschau', tr: 'Önizleme' },
  
  // Errors (page-level)
  'editProduct.errors.productIdRequired': { en: 'Product ID is required', de: 'Produkt-ID ist erforderlich', tr: 'Ürün kimliği gerekli' },
  'editProduct.errors.mustBeLoggedIn': { en: 'You must be logged in to edit products', de: 'Zum Bearbeiten von Produkten musst du angemeldet sein', tr: 'Ürünleri düzenlemek için giriş yapmalısınız' },
  'editProduct.errors.notFound': { en: 'Product not found', de: 'Produkt nicht gefunden', tr: 'Ürün bulunamadı' },
  'editProduct.errors.permissionDenied': { en: 'You do not have permission to edit this product', de: 'Du hast keine Berechtigung, dieses Produkt zu bearbeiten', tr: 'Bu ürünü düzenleme iznine sahip değilsiniz' },
  'editProduct.errors.failedToLoad': { en: 'Failed to load product data', de: 'Produktdaten konnten nicht geladen werden', tr: 'Ürün verileri yüklenemedi' },
  
  // UI texts (error page)
  'editProduct.errorPage.subtitle': { en: "The product you're trying to edit doesn't exist or you don't have permission to edit it.", de: 'Das Produkt, das du bearbeiten möchtest, existiert nicht oder du hast keine Berechtigung, es zu bearbeiten.', tr: 'Düzenlemeye çalıştığınız ürün mevcut değil veya düzenleme izniniz yok.' },
  
  // Validation messages (toasts)
  'editProduct.validation.nameMin': { en: 'Product name must be at least 3 characters', de: 'Der Produktname muss mindestens 3 Zeichen lang sein', tr: 'Ürün adı en az 3 karakter olmalıdır' },
  'editProduct.validation.descMin': { en: 'Product description must be at least 10 characters', de: 'Die Produktbeschreibung muss mindestens 10 Zeichen lang sein', tr: 'Ürün açıklaması en az 10 karakter olmalıdır' },
  'editProduct.validation.priceValid': { en: 'Please enter a valid price', de: 'Bitte einen gültigen Preis eingeben', tr: 'Lütfen geçerli bir fiyat girin' },
  'editProduct.validation.categoryRequired': { en: 'Please select a category', de: 'Bitte eine Kategorie auswählen', tr: 'Lütfen bir kategori seçin' },
  'editProduct.validation.subcategoryRequired': { en: 'Please select a subcategory', de: 'Bitte eine Unterkategorie auswählen', tr: 'Lütfen bir alt kategori seçin' },
  'editProduct.validation.colorRequired': { en: 'Please select at least one color', de: 'Bitte mindestens eine Farbe auswählen', tr: 'Lütfen en az bir renk seçin' },
  'editProduct.validation.sizeSystemRequired': { en: 'Please select a size system', de: 'Bitte ein Größensystem auswählen', tr: 'Lütfen bir beden sistemi seçin' },
  'editProduct.validation.sizeRequired': { en: 'Please select at least one size', de: 'Bitte mindestens eine Größe auswählen', tr: 'Lütfen en az bir beden seçin' },
  'editProduct.validation.imageRequired': { en: 'Please upload at least one image', de: 'Bitte mindestens ein Bild hochladen', tr: 'Lütfen en az bir görsel yükleyin' },
  'editProduct.validation.contentBlocked': { en: 'Content blocked: Your {fields} contains prohibited content. Please review and modify your content.', de: 'Inhalt blockiert: Deine {fields} enthalten unerlaubte Inhalte. Bitte überprüfe und passe deinen Inhalt an.', tr: 'İçerik engellendi: {fields} yasaklı içerik içeriyor. Lütfen içeriğinizi gözden geçirip değiştirin.' },
  'editProduct.validation.contentFlagged': { en: 'Content flagged for review: Your {fields} may require admin review.', de: 'Inhalt zur Überprüfung markiert: Deine {fields} müssen möglicherweise von einem Admin geprüft werden.', tr: 'İçerik inceleme için işaretlendi: {fields} yönetici incelemesi gerektirebilir.' },
  'editProduct.validation.priceTooLowForFees': { en: 'Price is too low to cover platform fees. Please increase the price.', de: 'Der Preis ist zu niedrig, um die Plattformgebühren zu decken. Bitte erhöhe den Preis.', tr: 'Fiyat, platform ücretlerini karşılamak için çok düşük. Lütfen fiyatı artırın.' },
  'editProduct.validation.mustBeLoggedInToUpdate': { en: 'You must be logged in to update products', de: 'Zum Aktualisieren von Produkten musst du angemeldet sein', tr: 'Ürünleri güncellemek için giriş yapmalısınız' },
  
  // Misc toasts and helper texts
  'editProduct.toast.imageAddedFromDocs': { en: 'Image added from documents!', de: 'Bild aus Dokumenten hinzugefügt!', tr: 'Görsel belgelerden eklendi!' },
  'editProduct.toast.imageAlreadyAdded': { en: 'This image is already added or URL is not available', de: 'Dieses Bild wurde bereits hinzugefügt oder die URL ist nicht verfügbar', tr: 'Bu görsel zaten eklendi veya URL mevcut değil' },
  'editProduct.toast.selectImageFile': { en: 'Please select an image file from your documents', de: 'Bitte wähle eine Bilddatei aus deinen Dokumenten', tr: 'Lütfen belgelerinizden bir görsel dosyası seçin' },
  'editProduct.toast.cameraAccessError': { en: 'Could not access camera. Please check permissions and try again.', de: 'Kein Zugriff auf die Kamera. Bitte Berechtigungen prüfen und erneut versuchen.', tr: 'Kameraya erişilemedi. Lütfen izinleri kontrol edip tekrar deneyin.' },
  'editProduct.delivery.loadingFailed': { en: 'Failed to load delivery options', de: 'Versandoptionen konnten nicht geladen werden', tr: 'Teslimat seçenekleri yüklenemedi' },
  'editProduct.categories.loadingFailed': { en: 'Failed to load categories', de: 'Kategorien konnten nicht geladen werden', tr: 'Kategoriler yüklenemedi' },
  'editProduct.subcategories.loadingFailed': { en: 'Failed to load subcategories', de: 'Unterkategorien konnten nicht geladen werden', tr: 'Alt kategoriler yüklenemedi' },
  'editProduct.loading.productData': { en: 'Loading product data...', de: 'Produktdaten werden geladen...', tr: 'Ürün verileri yükleniyor...' },
  // Form labels and UI
  'editProduct.form.section.basic': { en: 'Basic Information', de: 'Grundinformationen', tr: 'Temel Bilgiler' },
  'editProduct.form.section.detailsImages': { en: 'Details & Images', de: 'Details & Bilder', tr: 'Detaylar ve Görseller' },
  'editProduct.form.colors': { en: 'Colors', de: 'Farben', tr: 'Renkler' },
  'editProduct.form.colors.standard': { en: 'Standard Colors', de: 'Standardfarben', tr: 'Standart Renkler' },
  'editProduct.form.colors.custom': { en: 'Custom Colors', de: 'Benutzerdefinierte Farben', tr: 'Özel Renkler' },
  'editProduct.form.colors.addCustom': { en: 'Add Custom Color', de: 'Benutzerdefinierte Farbe hinzufügen', tr: 'Özel Renk Ekle' },
  'editProduct.form.colors.selectCustom': { en: 'Select Custom Color', de: 'Benutzerdefinierte Farbe auswählen', tr: 'Özel Renk Seç' },
  'editProduct.form.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'editProduct.form.addColor': { en: 'Add Color', de: 'Farbe hinzufügen', tr: 'Renk Ekle' },
  'editProduct.form.sizeSystem': { en: 'Size System', de: 'Größensystem', tr: 'Beden Sistemi' },
  'editProduct.form.selectSizeSystem': { en: 'Select Size System', de: 'Größensystem auswählen', tr: 'Beden Sistemi Seç' },
  'editProduct.form.category': { en: 'Category', de: 'Kategorie', tr: 'Kategori' },
  'editProduct.form.selectCategory': { en: 'Select Category', de: 'Kategorie auswählen', tr: 'Kategori Seç' },
  'editProduct.form.subcategory': { en: 'Subcategory', de: 'Unterkategorie', tr: 'Alt Kategori' },
  'editProduct.form.selectSubcategory': { en: 'Select Subcategory', de: 'Unterkategorie auswählen', tr: 'Alt Kategori Seç' },
  'editProduct.form.noSubcategories': { en: 'No subcategories available for this category.', de: 'Für diese Kategorie sind keine Unterkategorien verfügbar.', tr: 'Bu kategori için alt kategori yok.' },
  'editProduct.form.retry': { en: 'Retry', de: 'Wiederholen', tr: 'Tekrar Dene' },
  'editProduct.form.productName': { en: 'Product Name', de: 'Produktname', tr: 'Ürün Adı' },
  'editProduct.form.productName.placeholder': { en: 'Enter a descriptive name for your product', de: 'Gib einen aussagekräftigen Produktnamen ein', tr: 'Ürününüz için açıklayıcı bir ad girin' },
  'editProduct.form.productDescription': { en: 'Product Description', de: 'Produktbeschreibung', tr: 'Ürün Açıklaması' },
  'editProduct.form.productDescription.placeholder': { en: 'Describe your product in detail...', de: 'Beschreibe dein Produkt ausführlich...', tr: 'Ürününüzü ayrıntılı olarak açıklayın...' },
  // Upload/crop toasts
  'editProduct.toast.loginToUpload': { en: 'You must be logged in to upload images', de: 'Du musst angemeldet sein, um Bilder hochzuladen', tr: 'Görsel yüklemek için giriş yapmalısınız' },
  'editProduct.toast.uploadCroppedFailed': { en: 'Failed to upload cropped image', de: 'Zugeschnittenes Bild konnte nicht hochgeladen werden', tr: 'Kırpılmış görsel yüklenemedi' },
  'editProduct.toast.croppedUploaded': { en: 'Image cropped and uploaded successfully!', de: 'Bild wurde zugeschnitten und erfolgreich hochgeladen!', tr: 'Görsel kırpıldı ve başarıyla yüklendi!' },
  'editProduct.toast.processCroppedFailed': { en: 'Failed to process cropped image', de: 'Zugeschnittenes Bild konnte nicht verarbeitet werden', tr: 'Kırpılmış görsel işlenemedi' },
  'productDetail.tabs.reviews': { en: 'Reviews', de: 'Bewertungen', tr: 'Değerlendirmeler' },
  'productDetail.tabs.shippingReturns': { en: 'Shipping & Returns', de: 'Versand & Rückgaben', tr: 'Kargo ve İadeler' },
  'jobDetail.tabs.description': { en: 'Job Description', de: 'Stellenbeschreibung', tr: 'İş Tanımı' },
  'jobDetail.tabs.requirements': { en: 'Requirements', de: 'Anforderungen', tr: 'Gereksinimler' },
  'jobDetail.tabs.benefits': { en: 'Benefits', de: 'Leistungen', tr: 'Yan Haklar' },
  // Footer extra
  'footer.legal.rules': { en: 'Platform Rules', de: 'Plattform-Regeln', tr: 'Platform Kuralları' },
  // Header extras
  'header.favorites': { en: 'Favorites', de: 'Favoriten', tr: 'Favoriler' },
  'header.cart': { en: 'Cart', de: 'Warenkorb', tr: 'Sepet' },
  'settings.password.subtitle': { en: 'Update your password to keep your account secure', de: 'Aktualisiere dein Passwort, um dein Konto zu schützen', tr: 'Hesabını güvende tutmak için şifreni güncelle' },
  'settings.password.current': { en: 'Current Password', de: 'Aktuelles Passwort', tr: 'Mevcut Şifre' },
  'settings.password.new': { en: 'New Password', de: 'Neues Passwort', tr: 'Yeni Şifre' },
  'settings.password.confirm': { en: 'Confirm New Password', de: 'Neues Passwort bestätigen', tr: 'Yeni Şifreyi Onayla' },
  'settings.password.requirements.title': { en: 'Password Requirements:', de: 'Passwortanforderungen:', tr: 'Şifre Gereksinimleri:' },
  'settings.password.requirements.minLength': { en: 'At least 8 characters', de: 'Mindestens 8 Zeichen', tr: 'En az 8 karakter' },
  'settings.password.requirements.upper': { en: 'Contains uppercase letter', de: 'Großbuchstabe enthalten', tr: 'Büyük harf içerir' },
  'settings.password.requirements.lower': { en: 'Contains lowercase letter', de: 'Kleinbuchstabe enthalten', tr: 'Küçük harf içerir' },
  'settings.password.requirements.number': { en: 'Contains number', de: 'Zahl enthalten', tr: 'Rakam içerir' },
  'settings.password.requirements.special': { en: 'Contains special character', de: 'Sonderzeichen enthalten', tr: 'Özel karakter içerir' },
  'settings.password.button.updating': { en: 'Updating...', de: 'Aktualisiere...', tr: 'Güncelleniyor...' },
  'settings.password.button.update': { en: 'Update Password', de: 'Passwort aktualisieren', tr: 'Şifreyi Güncelle' },
  'settings.password.error.mismatch': { en: 'New passwords do not match', de: 'Neue Passwörter stimmen nicht überein', tr: 'Yeni şifreler eşleşmiyor' },
  'settings.password.error.minlength': { en: 'Password must be at least 8 characters long', de: 'Passwort muss mindestens 8 Zeichen lang sein', tr: 'Şifre en az 8 karakter olmalıdır' },
  'settings.password.success.updated': { en: 'Password updated successfully!', de: 'Passwort erfolgreich aktualisiert!', tr: 'Şifre başarıyla güncellendi!' },
  // Notifications Tab
  'settings.notifications.title': { en: 'Notification Preferences', de: 'Benachrichtigungseinstellungen', tr: 'Bildirim Tercihleri' },
  'settings.notifications.subtitle': { en: 'Choose how you want to be notified about updates and activities', de: 'Wählen Sie, wie Sie über Updates und Aktivitäten benachrichtigt werden möchten', tr: 'Güncellemeler ve aktiviteler için nasıl bilgilendirilmek istediğinizi seçin' },
  'settings.notifications.category.email': { en: 'Email Notifications', de: 'E-Mail-Benachrichtigungen', tr: 'E-posta Bildirimleri' },
  'settings.notifications.category.push': { en: 'Push Notifications', de: 'Push-Benachrichtigungen', tr: 'Anlık Bildirimler' },
  'settings.notifications.category.sms': { en: 'SMS Notifications', de: 'SMS-Benachrichtigungen', tr: 'SMS Bildirimleri' },
  'settings.notifications.setting.orderUpdates.label': { en: 'Order Updates', de: 'Bestellaktualisierungen', tr: 'Sipariş Güncellemeleri' },
  'settings.notifications.setting.orderUpdates.desc': { en: 'Get notified about order status changes', de: 'Über Änderungen des Bestellstatus informiert werden', tr: 'Sipariş durumu değişikliklerinden haberdar olun' },
  'settings.notifications.setting.promotions.label': { en: 'Promotions & Offers', de: 'Aktionen & Angebote', tr: 'Kampanyalar ve Teklifler' },
  'settings.notifications.setting.promotions.desc': { en: 'Receive special deals and discounts', de: 'Sonderangebote und Rabatte erhalten', tr: 'Özel fırsatlar ve indirimler alın' },
  'settings.notifications.setting.securityAlerts.label': { en: 'Security Alerts', de: 'Sicherheitswarnungen', tr: 'Güvenlik Uyarıları' },
  'settings.notifications.setting.securityAlerts.desc': { en: 'Important security notifications', de: 'Wichtige Sicherheitsbenachrichtigungen', tr: 'Önemli güvenlik bildirimleri' },
  'settings.notifications.setting.weeklyDigest.label': { en: 'Weekly Digest', de: 'Wöchentliche Übersicht', tr: 'Haftalık Özet' },
  'settings.notifications.setting.weeklyDigest.desc': { en: 'Weekly summary of your activity', de: 'Wöchentliche Zusammenfassung Ihrer Aktivitäten', tr: 'Haftalık etkinlik özeti' },
  'settings.notifications.setting.newMessages.label': { en: 'New Messages', de: 'Neue Nachrichten', tr: 'Yeni Mesajlar' },
  'settings.notifications.setting.newMessages.desc': { en: 'When you receive new messages', de: 'Wenn Sie neue Nachrichten erhalten', tr: 'Yeni mesaj aldığınızda' },
  'settings.notifications.setting.orderStatus.label': { en: 'Order Status', de: 'Bestellstatus', tr: 'Sipariş Durumu' },
  'settings.notifications.setting.orderStatus.desc': { en: 'Real-time order updates', de: 'Echtzeit-Bestellupdates', tr: 'Anlık sipariş güncellemeleri' },
  'settings.notifications.setting.priceDrops.label': { en: 'Price Drops', de: 'Preissenkungen', tr: 'Fiyat Düşüşleri' },
  'settings.notifications.setting.priceDrops.desc': { en: 'When items you like go on sale', de: 'Wenn Ihre Lieblingsartikel im Angebot sind', tr: 'Beğendiğiniz ürünler indirime girdiğinde' },
  'settings.notifications.setting.recommendations.label': { en: 'Recommendations', de: 'Empfehlungen', tr: 'Öneriler' },
  'settings.notifications.setting.recommendations.desc': { en: 'Personalized product suggestions', de: 'Personalisierte Produktempfehlungen', tr: 'Kişiselleştirilmiş ürün önerileri' },
  'settings.notifications.setting.orderConfirmations.label': { en: 'Order Confirmations', de: 'Bestellbestätigungen', tr: 'Sipariş Onayları' },
  'settings.notifications.setting.orderConfirmations.desc': { en: 'SMS confirmation for orders', de: 'SMS-Bestätigung für Bestellungen', tr: 'Siparişler için SMS onayı' },
  'settings.notifications.setting.smsPromotions.label': { en: 'Promotions', de: 'Aktionen', tr: 'Kampanyalar' },
  'settings.notifications.setting.smsPromotions.desc': { en: 'Special offers via SMS', de: 'Sonderangebote per SMS', tr: 'SMS ile özel teklifler' },
  'settings.notifications.chip.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'settings.notifications.proTip.title': { en: 'Pro Tip', de: 'Profi-Tipp', tr: 'İpucu' },
  'settings.notifications.proTip.text': { en: 'You can always change these settings later. We recommend keeping security alerts enabled to stay informed about important account activities.', de: 'Sie können diese Einstellungen jederzeit ändern. Wir empfehlen, Sicherheitswarnungen aktiviert zu lassen, um über wichtige Kontoaktivitäten informiert zu bleiben.', tr: 'Bu ayarları daha sonra her zaman değiştirebilirsiniz. Önemli hesap aktivitelerinden haberdar olmak için güvenlik uyarılarını açık tutmanızı öneririz.' },
  // Payment Methods Tab
  'settings.payments.title': { en: 'Payment Methods', de: 'Zahlungsmethoden', tr: 'Ödeme Yöntemleri' },
  'settings.payments.subtitle': { en: 'Manage your payment methods for secure transactions', de: 'Sichere Transaktionen für deine Zahlungsmethoden verwalten', tr: 'Güvenli işlemler için ödeme yöntemlerini yönet' },
  'settings.payments.buttons.testPayment': { en: 'Test Payment', de: 'Testzahlung', tr: 'Test Ödemesi' },
  'settings.payments.buttons.addPayPal': { en: 'Add PayPal', de: 'PayPal hinzufügen', tr: 'PayPal Ekle' },
  'settings.payments.buttons.addMethod': { en: 'Add Method', de: 'Methode hinzufügen', tr: 'Yöntem Ekle' },
  'settings.payments.paypal.title': { en: 'PayPal Accounts', de: 'PayPal-Konten', tr: 'PayPal Hesapları' },
  'settings.payments.paypal.subtitle': { en: 'Manage your PayPal payment methods', de: 'PayPal-Zahlungsmethoden verwalten', tr: 'PayPal ödeme yöntemlerini yönet' },
  'settings.payments.chip.default': { en: 'Default', de: 'Standard', tr: 'Varsayılan' },
  'settings.payments.actions.setDefault': { en: 'Set as Default', de: 'Als Standard festlegen', tr: 'Varsayılan Yap' },
  'settings.payments.actions.edit': { en: 'Edit', de: 'Bearbeiten', tr: 'Düzenle' },
  'settings.payments.actions.remove': { en: 'Remove', de: 'Entfernen', tr: 'Kaldır' },
  'settings.payments.expires': { en: 'Expires {date}', de: 'Gültig bis {date}', tr: '{date} tarihinde sona erer' },
  'settings.payments.security.title': { en: 'Security & Privacy', de: 'Sicherheit & Datenschutz', tr: 'Güvenlik ve Gizlilik' },
  'settings.payments.security.text': { en: 'All payment methods are encrypted and stored securely. We never store your full card details and use industry-standard security measures to protect your information.', de: 'Alle Zahlungsmethoden werden verschlüsselt und sicher gespeichert. Wir speichern niemals deine vollständigen Kartendaten und verwenden branchenübliche Sicherheitsmaßnahmen, um deine Informationen zu schützen.', tr: 'Tüm ödeme yöntemleri şifrelenir ve güvenle saklanır. Tam kart bilgilerinizi asla saklamayız ve bilgilerinizi korumak için endüstri standardı güvenlik önlemleri kullanırız.' },
  // Dialogs
  'settings.payments.addDialog.title': { en: 'Add Payment Method', de: 'Zahlungsmethode hinzufügen', tr: 'Ödeme Yöntemi Ekle' },
  'settings.payments.addDialog.typeLabel': { en: 'Payment Method Type', de: 'Zahlungsmethode', tr: 'Ödeme Yöntemi Türü' },
  'settings.payments.type.card': { en: 'Credit/Debit Card', de: 'Kredit-/Debitkarte', tr: 'Kredi/Banka Kartı' },
  'settings.payments.type.paypal': { en: 'PayPal Account', de: 'PayPal-Konto', tr: 'PayPal Hesabı' },
  'settings.payments.fields.cardNumber': { en: 'Card Number', de: 'Kartennummer', tr: 'Kart Numarası' },
  'settings.payments.fields.expiryDate': { en: 'Expiry Date', de: 'Ablaufdatum', tr: 'Son Kullanma Tarihi' },
  'settings.payments.fields.cvv': { en: 'CVV', de: 'CVV', tr: 'CVV' },
  'settings.payments.fields.cardholderName': { en: 'Cardholder Name', de: 'Karteninhaber', tr: 'Kart Sahibi Adı' },
  'settings.payments.fields.paypalEmail': { en: 'PayPal Email', de: 'PayPal-E-Mail', tr: 'PayPal E-postası' },
  'settings.payments.fields.accountName': { en: 'Account Name', de: 'Kontoname', tr: 'Hesap Adı' },
  'settings.common.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'settings.payments.addDialog.submit': { en: 'Add Method', de: 'Methode hinzufügen', tr: 'Yöntem Ekle' },
  'settings.payments.editDialog.title': { en: 'Edit Payment Method', de: 'Zahlungsmethode bearbeiten', tr: 'Ödeme Yöntemini Düzenle' },
  'settings.payments.editDialog.submit': { en: 'Update Method', de: 'Methode aktualisieren', tr: 'Yöntemi Güncelle' },
  // Toasts
  'settings.payments.toast.fillCard': { en: 'Please fill in all card details', de: 'Bitte alle Kartendaten ausfüllen', tr: 'Lütfen tüm kart bilgilerini doldurun' },
  'settings.payments.toast.fillPayPal': { en: 'Please fill in PayPal email and account name', de: 'Bitte PayPal-E-Mail und Kontonamen ausfüllen', tr: 'Lütfen PayPal e-postası ve hesap adını doldurun' },
  'settings.payments.toast.added': { en: 'Payment method added successfully!', de: 'Zahlungsmethode erfolgreich hinzugefügt!', tr: 'Ödeme yöntemi başarıyla eklendi!' },
  'settings.payments.toast.addFailed': { en: 'Failed to add payment method', de: 'Zahlungsmethode konnte nicht hinzugefügt werden', tr: 'Ödeme yöntemi eklenemedi' },
  'settings.payments.toast.updated': { en: 'Payment method updated successfully!', de: 'Zahlungsmethode erfolgreich aktualisiert!', tr: 'Ödeme yöntemi başarıyla güncellendi!' },
  'settings.payments.toast.updateFailed': { en: 'Failed to update payment method', de: 'Zahlungsmethode konnte nicht aktualisiert werden', tr: 'Ödeme yöntemi güncellenemedi' },
  'settings.payments.toast.defaultUpdated': { en: 'Default payment method updated!', de: 'Standard-Zahlungsmethode aktualisiert!', tr: 'Varsayılan ödeme yöntemi güncellendi!' },
  'settings.payments.toast.defaultFailed': { en: 'Failed to set default payment method', de: 'Standard-Zahlungsmethode konnte nicht festgelegt werden', tr: 'Varsayılan ödeme yöntemi ayarlanamadı' },
  'settings.payments.toast.removed': { en: 'Payment method removed!', de: 'Zahlungsmethode entfernt!', tr: 'Ödeme yöntemi kaldırıldı!' },
  'settings.payments.toast.removeFailed': { en: 'Failed to remove payment method', de: 'Zahlungsmethode konnte nicht entfernt werden', tr: 'Ödeme yöntemi kaldırılamadı' },
  // Addresses Tab
  'settings.addresses.title': { en: 'Addresses', de: 'Adressen', tr: 'Adresler' },
  'settings.addresses.subtitle': { en: 'Manage your shipping and billing addresses', de: 'Versand- und Rechnungsadressen verwalten', tr: 'Gönderim ve fatura adreslerini yönet' },
  'settings.addresses.buttons.findOnMap': { en: 'Find on Map', de: 'Auf der Karte finden', tr: 'Haritada Bul' },
  'settings.addresses.buttons.searchAddress': { en: 'Search Address', de: 'Adresse suchen', tr: 'Adres Ara' },
  'settings.addresses.buttons.addAddress': { en: 'Add Address', de: 'Adresse hinzufügen', tr: 'Adres Ekle' },
  'settings.addresses.empty.title': { en: 'No Addresses Yet', de: 'Noch keine Adressen', tr: 'Henüz Adres Yok' },
  'settings.addresses.empty.subtitle': { en: 'Add your first address to get started with shipping and billing', de: 'Fügen Sie Ihre erste Adresse für Versand und Abrechnung hinzu', tr: 'Gönderim ve fatura için ilk adresinizi ekleyin' },
  'settings.addresses.empty.button': { en: 'Add Your First Address', de: 'Erste Adresse hinzufügen', tr: 'İlk Adresini Ekle' },
  'settings.addresses.chip.default': { en: 'Default', de: 'Standard', tr: 'Varsayılan' },
  'settings.addresses.actions.edit': { en: 'Edit', de: 'Bearbeiten', tr: 'Düzenle' },
  'settings.addresses.actions.setDefault': { en: 'Set Default', de: 'Als Standard festlegen', tr: 'Varsayılan Yap' },
  'settings.addresses.dialog.add.title': { en: 'Add New Address', de: 'Neue Adresse hinzufügen', tr: 'Yeni Adres Ekle' },
  'settings.addresses.dialog.edit.title': { en: 'Edit Address', de: 'Adresse bearbeiten', tr: 'Adresi Düzenle' },
  'settings.addresses.dialog.generate': { en: 'Generate Random', de: 'Zufällig erzeugen', tr: 'Rastgele Oluştur' },
  'settings.addresses.successFound.title': { en: 'Address Successfully Found! 🎉', de: 'Adresse erfolgreich gefunden! 🎉', tr: 'Adres Başarıyla Bulundu! 🎉' },
  'settings.addresses.successFound.text.verified': { en: 'This address was verified and found on the map. You can now add it to your addresses.', de: 'Diese Adresse wurde auf der Karte verifiziert und gefunden. Sie können sie jetzt zu Ihren Adressen hinzufügen.', tr: 'Bu adres haritada doğrulandı ve bulundu. Şimdi adreslerinize ekleyebilirsiniz.' },
  'settings.addresses.successFound.text.selected': { en: 'This address was selected from the map. You can now add it to your addresses.', de: 'Diese Adresse wurde von der Karte ausgewählt. Sie können sie jetzt zu Ihren Adressen hinzufügen.', tr: 'Bu adres haritadan seçildi. Şimdi adreslerinize ekleyebilirsiniz.' },
  'settings.addresses.successFound.text.validated': { en: 'This address was verified through map search. You can now add it to your addresses.', de: 'Diese Adresse wurde durch die Kartensuche verifiziert. Sie können sie jetzt zu Ihren Adressen hinzufügen.', tr: 'Bu adres harita araması ile doğrulandı. Şimdi adreslerinize ekleyebilirsiniz.' },
  'settings.addresses.coords.title': { en: 'Map Coordinates', de: 'Koordinaten', tr: 'Harita Koordinatları' },
  'settings.addresses.coords.latitude': { en: 'Latitude', de: 'Breitengrad', tr: 'Enlem' },
  'settings.addresses.coords.longitude': { en: 'Longitude', de: 'Längengrad', tr: 'Boylam' },
  'settings.addresses.coords.note': { en: 'Coordinates from map selection (read-only)', de: 'Koordinaten aus Kartenauswahl (nur Lesen)', tr: 'Harita seçiminden koordinatlar (salt okunur)' },
  'settings.addresses.fields.addressTitle': { en: 'Address Title', de: 'Adressbezeichnung', tr: 'Adres Başlığı' },
  'settings.addresses.fields.addressTitle.ph': { en: 'e.g., Home, Work, Billing', de: 'z. B. Zuhause, Arbeit, Rechnung', tr: 'örn. Ev, İş, Fatura' },
  'settings.addresses.fields.fullName': { en: 'Full Name', de: 'Vollständiger Name', tr: 'Ad Soyad' },
  'settings.addresses.fields.company': { en: 'Company (Optional)', de: 'Firma (optional)', tr: 'Şirket (İsteğe bağlı)' },
  'settings.addresses.fields.address1': { en: 'Address Line 1', de: 'Adresszeile 1', tr: 'Adres Satırı 1' },
  'settings.addresses.fields.address1.ph': { en: 'Street address, P.O. box', de: 'Straße, Postfach', tr: 'Sokak adresi, P.K.' },
  'settings.addresses.fields.address2': { en: 'Address Line 2', de: 'Adresszeile 2', tr: 'Adres Satırı 2' },
  'settings.addresses.fields.address2.ph': { en: 'Apartment, suite, unit, building, floor, etc.', de: 'Wohnung, Suite, Einheit, Gebäude, Etage, etc.', tr: 'Daire, suite, birim, bina, kat vb.' },
  'settings.addresses.fields.city': { en: 'City', de: 'Stadt', tr: 'Şehir' },
  'settings.addresses.fields.state': { en: 'State/Province', de: 'Bundesland/Provinz', tr: 'Eyalet/İl' },
  'settings.addresses.fields.postalCode': { en: 'Postal Code', de: 'Postleitzahl', tr: 'Posta Kodu' },
  'settings.addresses.fields.country': { en: 'Country', de: 'Land', tr: 'Ülke' },
  'settings.addresses.fields.phone': { en: 'Phone (Optional)', de: 'Telefon (optional)', tr: 'Telefon (Opsiyonel)' },
  'settings.addresses.switch.default': { en: 'Set as default address', de: 'Als Standardadresse festlegen', tr: 'Varsayılan adres olarak ayarla' },
  'settings.addresses.dialog.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'settings.addresses.dialog.add.saving': { en: 'Saving Address...', de: 'Adresse wird gespeichert...', tr: 'Adres Kaydediliyor...' },
  'settings.addresses.dialog.add.verified': { en: '✅ Add Verified Address', de: '✅ Verifizierte Adresse hinzufügen', tr: '✅ Doğrulanmış Adresi Ekle' },
  'settings.addresses.dialog.add.submit': { en: 'Add Address', de: 'Adresse hinzufügen', tr: 'Adres Ekle' },
  'settings.addresses.dialog.edit.submit': { en: 'Update Address', de: 'Adresse aktualisieren', tr: 'Adresi Güncelle' },
  'settings.addresses.mapLocation': { en: 'Map Location', de: 'Kartenstandort', tr: 'Harita Konumu' },
  // Toasts
  'settings.addresses.toast.loadFailed': { en: 'Failed to load addresses', de: 'Adressen konnten nicht geladen werden', tr: 'Adresler yüklenemedi' },
  'settings.addresses.toast.added': { en: 'Address added successfully!', de: 'Adresse erfolgreich hinzugefügt!', tr: 'Adres başarıyla eklendi!' },
  'settings.addresses.toast.addFailed': { en: 'Failed to add address', de: 'Adresse konnte nicht hinzugefügt werden', tr: 'Adres eklenemedi' },
  'settings.addresses.toast.updated': { en: 'Address updated successfully!', de: 'Adresse erfolgreich aktualisiert!', tr: 'Adres başarıyla güncellendi!' },
  'settings.addresses.toast.updateFailed': { en: 'Failed to update address', de: 'Adresse konnte nicht aktualisiert werden', tr: 'Adres güncellenemedi' },
  'settings.addresses.toast.deleteConfirm': { en: 'Are you sure you want to delete this address?', de: 'Möchten Sie diese Adresse wirklich löschen?', tr: 'Bu adresi silmek istediğinizden emin misiniz?' },
  'settings.addresses.toast.deleted': { en: 'Address deleted successfully!', de: 'Adresse erfolgreich gelöscht!', tr: 'Adres başarıyla silindi!' },
  'settings.addresses.toast.deleteFailed': { en: 'Failed to delete address', de: 'Adresse konnte nicht gelöscht werden', tr: 'Adres silinemedi' },
  'settings.addresses.toast.defaultUpdated': { en: 'Default address updated!', de: 'Standardadresse aktualisiert!', tr: 'Varsayılan adres güncellendi!' },
  'settings.addresses.toast.defaultFailed': { en: 'Failed to update default address', de: 'Standardadresse konnte nicht aktualisiert werden', tr: 'Varsayılan adres güncellenemedi' },
  'settings.addresses.toast.mapSelected': { en: 'Address selected from map! Please complete the form.', de: 'Adresse von der Karte ausgewählt! Bitte das Formular ausfüllen.', tr: 'Adres haritadan seçildi! Lütfen formu doldurun.' },
  'settings.addresses.toast.mapVerifySuccess': { en: 'Address verified! You can now add it to your addresses.', de: 'Adresse verifiziert! Sie können sie jetzt zu Ihren Adressen hinzufügen.', tr: 'Adres doğrulandı! Şimdi adreslerinize ekleyebilirsiniz.' },
  'settings.addresses.toast.mapVerifyFail': { en: 'Address not found! Please verify the address details or try a different location.', de: 'Adresse nicht gefunden! Bitte überprüfen Sie die Adressdetails oder versuchen Sie einen anderen Ort.', tr: 'Adres bulunamadı! Lütfen adres ayrıntılarını doğrulayın veya başka bir konum deneyin.' },
  'settings.addresses.toast.gen.loading': { en: 'Generating real German address...', de: 'Echte deutsche Adresse wird generiert...', tr: 'Gerçek Alman adresi oluşturuluyor...' },
  'settings.addresses.toast.gen.success': { en: 'Real German address generated! ✨', de: 'Echte deutsche Adresse generiert! ✨', tr: 'Gerçek Alman adresi oluşturuldu! ✨' },
  'settings.addresses.toast.gen.failed': { en: 'Failed to generate address.', de: 'Adresse konnte nicht generiert werden.', tr: 'Adres oluşturulamadı.' },
  'settings.addresses.toast.randomGenerated': { en: 'Random address generated! ✨', de: 'Zufällige Adresse generiert! ✨', tr: 'Rastgele adres oluşturuldu! ✨' },
  // API Keys Tab
  'settings.apiKeys.title': { en: 'API Keys Management', de: 'API-Schlüssel Verwaltung', tr: 'API Anahtarı Yönetimi' },
  'settings.apiKeys.subtitle.user': { en: 'Manage your personal API keys for various AI services.', de: 'Verwalte deine persönlichen API-Schlüssel für verschiedene KI-Dienste.', tr: 'Çeşitli yapay zeka servisleri için kişisel API anahtarlarını yönet.' },
  'settings.apiKeys.subtitle.company': { en: 'Manage API keys for your company. These keys can be shared with team members.', de: 'Verwalte API-Schlüssel für dein Unternehmen. Diese Schlüssel können mit Teammitgliedern geteilt werden.', tr: 'Şirketiniz için API anahtarlarını yönetin. Bu anahtarlar ekip üyeleriyle paylaşılabilir.' },
  'settings.apiKeys.loading': { en: 'Loading API keys...', de: 'API-Schlüssel werden geladen...', tr: 'API anahtarları yükleniyor...' },
  'settings.apiKeys.addNew': { en: 'Add New API Key', de: 'Neuen API-Schlüssel hinzufügen', tr: 'Yeni API Anahtarı Ekle' },
  'settings.apiKeys.chip.companyDashboard': { en: 'Company Dashboard', de: 'Unternehmens-Dashboard', tr: 'Şirket Paneli' },
  'settings.apiKeys.empty.title': { en: 'No API Keys Found', de: 'Keine API-Schlüssel gefunden', tr: 'API Anahtarı Bulunamadı' },
  'settings.apiKeys.empty.subtitle.user': { en: 'Add your first personal API key to get started.', de: 'Füge deinen ersten persönlichen API-Schlüssel hinzu.', tr: 'Başlamak için ilk kişisel API anahtarını ekle.' },
  'settings.apiKeys.empty.subtitle.company': { en: 'Add your first company API key to get started.', de: 'Füge deinen ersten Unternehmens-API-Schlüssel hinzu.', tr: 'Başlamak için ilk şirket API anahtarını ekle.' },
  'settings.apiKeys.empty.add': { en: 'Add API Key', de: 'API-Schlüssel hinzufügen', tr: 'API Anahtarı Ekle' },
  'settings.apiKeys.label.company': { en: 'Company', de: 'Unternehmen', tr: 'Şirket' },
  'settings.apiKeys.label.personal': { en: 'Personal', de: 'Persönlich', tr: 'Kişisel' },
  'settings.apiKeys.status.active': { en: 'Active', de: 'Aktiv', tr: 'Aktif' },
  'settings.apiKeys.status.inactive': { en: 'Inactive', de: 'Inaktiv', tr: 'Pasif' },
  'settings.apiKeys.field.apiKey': { en: 'API Key:', de: 'API-Schlüssel:', tr: 'API Anahtarı:' },
  'settings.apiKeys.tooltip.hide': { en: 'Hide API Key', de: 'API-Schlüssel verbergen', tr: 'API Anahtarını Gizle' },
  'settings.apiKeys.tooltip.show': { en: 'Show API Key', de: 'API-Schlüssel anzeigen', tr: 'API Anahtarını Göster' },
  'settings.apiKeys.usage.usedBy': { en: 'Used by {count} user(s)', de: 'Verwendet von {count} Benutzer(n)', tr: '{count} kullanıcı tarafından kullanılıyor' },
  'settings.apiKeys.usage.lastUsed': { en: 'Last used: {date}', de: 'Zuletzt verwendet: {date}', tr: 'Son kullanım: {date}' },
  'settings.apiKeys.buttons.edit': { en: 'Edit', de: 'Bearbeiten', tr: 'Düzenle' },
  'settings.apiKeys.buttons.test': { en: 'Test', de: 'Testen', tr: 'Test' },
  'settings.apiKeys.buttons.deactivate': { en: 'Deactivate', de: 'Deaktivieren', tr: 'Devre Dışı Bırak' },
  'settings.apiKeys.buttons.activate': { en: 'Activate', de: 'Aktivieren', tr: 'Etkinleştir' },
  'settings.apiKeys.buttons.delete': { en: 'Delete', de: 'Löschen', tr: 'Sil' },
  // Add dialog
  'settings.apiKeys.add.title': { en: 'Add New API Key', de: 'Neuen API-Schlüssel hinzufügen', tr: 'Yeni API Anahtarı Ekle' },
  'settings.apiKeys.add.service': { en: 'Service', de: 'Dienst', tr: 'Servis' },
  'settings.apiKeys.add.apiKey': { en: 'API Key', de: 'API-Schlüssel', tr: 'API Anahtarı' },
  'settings.apiKeys.add.placeholder': { en: 'Enter your API key', de: 'Gib deinen API-Schlüssel ein', tr: 'API anahtarını girin' },
  'settings.apiKeys.add.note.company': { en: 'This API key will be shared with all company members.', de: 'Dieser API-Schlüssel wird mit allen Unternehmensmitgliedern geteilt.', tr: 'Bu API anahtarı tüm şirket üyeleriyle paylaşılacaktır.' },
  'settings.apiKeys.add.note': { en: 'Note: You can add multiple API keys for the same service. Each key must be unique.', de: 'Hinweis: Du kannst mehrere API-Schlüssel für denselben Dienst hinzufügen. Jeder Schlüssel muss eindeutig sein.', tr: 'Not: Aynı servis için birden çok API anahtarı ekleyebilirsin. Her anahtar benzersiz olmalıdır.' },
  'settings.apiKeys.common.cancel': { en: 'Cancel', de: 'Abbrechen', tr: 'İptal' },
  'settings.apiKeys.add.submit': { en: 'Add API Key', de: 'API-Schlüssel hinzufügen', tr: 'API Anahtarı Ekle' },
  'settings.apiKeys.add.adding': { en: 'Adding...', de: 'Wird hinzugefügt...', tr: 'Ekleniyor...' },
  // Edit dialog
  'settings.apiKeys.edit.title': { en: 'Edit API Key', de: 'API-Schlüssel bearbeiten', tr: 'API Anahtarını Düzenle' },
  'settings.apiKeys.edit.service': { en: 'Service', de: 'Dienst', tr: 'Servis' },
  'settings.apiKeys.edit.submit': { en: 'Update API Key', de: 'API-Schlüssel aktualisieren', tr: 'API Anahtarını Güncelle' },
  'settings.apiKeys.edit.updating': { en: 'Updating...', de: 'Wird aktualisiert...', tr: 'Güncelleniyor...' },
  // Delete dialog
  'settings.apiKeys.delete.title': { en: 'Delete API Key', de: 'API-Schlüssel löschen', tr: 'API Anahtarını Sil' },
  'settings.apiKeys.delete.confirm': { en: 'Are you sure you want to delete this API key? This action cannot be undone.', de: 'Möchtest du diesen API-Schlüssel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.', tr: 'Bu API anahtarını silmek istediğine emin misin? Bu işlem geri alınamaz.' },
  'settings.apiKeys.delete.service': { en: 'Service:', de: 'Dienst:', tr: 'Servis:' },
  'settings.apiKeys.delete.type': { en: 'Type:', de: 'Typ:', tr: 'Tür:' },
  'settings.apiKeys.delete.type.company': { en: 'Company', de: 'Unternehmen', tr: 'Şirket' },
  'settings.apiKeys.delete.type.personal': { en: 'Personal', de: 'Persönlich', tr: 'Kişisel' },
  'settings.apiKeys.delete.deleting': { en: 'Deleting...', de: 'Wird gelöscht...', tr: 'Siliniyor...' },
  'settings.apiKeys.delete.submit': { en: 'Delete API Key', de: 'API-Schlüssel löschen', tr: 'API Anahtarını Sil' },
  // Toasts
  'settings.apiKeys.toast.loadFailed': { en: 'Failed to load API keys', de: 'API-Schlüssel konnten nicht geladen werden', tr: 'API anahtarları yüklenemedi' },
  'settings.apiKeys.toast.added': { en: 'API key added successfully!', de: 'API-Schlüssel erfolgreich hinzugefügt!', tr: 'API anahtarı başarıyla eklendi!' },
  'settings.apiKeys.toast.addFailed': { en: 'Failed to add API key', de: 'API-Schlüssel konnte nicht hinzugefügt werden', tr: 'API anahtarı eklenemedi' },
  'settings.apiKeys.toast.updated': { en: 'API key updated successfully!', de: 'API-Schlüssel erfolgreich aktualisiert!', tr: 'API anahtarı başarıyla güncellendi!' },
  'settings.apiKeys.toast.updateFailed': { en: 'Failed to update API key', de: 'API-Schlüssel konnte nicht aktualisiert werden', tr: 'API anahtarı güncellenemedi' },
  'settings.apiKeys.toast.deleted': { en: 'API key deleted successfully!', de: 'API-Schlüssel erfolgreich gelöscht!', tr: 'API anahtarı başarıyla silindi!' },
  'settings.apiKeys.toast.deleteFailed': { en: 'Failed to delete API key', de: 'API-Schlüssel konnte nicht gelöscht werden', tr: 'API anahtarı silinemedi' },
  'settings.apiKeys.toast.toggled': { en: 'API key {state} successfully!', de: 'API-Schlüssel erfolgreich {state}!', tr: 'API anahtarı başarıyla {state}!' },
  'settings.apiKeys.toggled.activated': { en: 'activated', de: 'aktiviert', tr: 'etkinleştirildi' },
  'settings.apiKeys.toggled.deactivated': { en: 'deactivated', de: 'deaktiviert', tr: 'devre dışı bırakıldı' },
};

interface TranslationProviderProps {
  children: ReactNode;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('ui_language');
    return (saved === 'de' || saved === 'tr' || saved === 'en') ? saved : 'en';
  });

  const [autoTranslate, setAutoTranslateState] = useState<boolean>(() => {
    const saved = localStorage.getItem('auto_translate');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('ui_language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('auto_translate', autoTranslate.toString());
  }, [autoTranslate]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const setAutoTranslate = (enabled: boolean) => {
    setAutoTranslateState(enabled);
  };

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }
    return translation[language] || translation.en || key;
  };

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t, autoTranslate, setAutoTranslate }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

